import React, { useState } from 'react';
import {
  getSchemaForSection,
  isHiddenExtractedKey,
  isRetiredExtraKey,
  extraDataHeading,
  HIDE_EXTRA_DATA_PANEL,
  isSchemaFieldHidden,
  coerceFieldValue,
  formatDisplayValue,
  looksLikeFormJsonBlob,
  sanitizeAssessmentNarrative,
  looksLikePromptEcho,
  COMPLIANCE_CHECKLIST_STATUSES,
  normalizeChartProfilesList,
  safeNormalizeSectionExtractedData,
} from '../../constants/complianceSectionSchemas';
import { formatChecklistDate } from '../../utils/checklistFormatting';
import DatePicker from '../common/DatePicker';
import CitationChip from '../common/citations/CitationChip';
import CitationContext from '../common/citations/CitationContext';
import { firstQuotePage, quoteForPage, resolveFieldCite, rowCite } from '../common/citations/citationModel';

/** Fields never expected to carry a page (identity echoes, computed checklists). */
const CITE_EXEMPT_KEYS = new Set([
  'student_name', 'validation_results', 'checkbox_selected', 'age',
  'iep_compliance_checklist', 'transition_checklist',
]);

/** Page chip for one table row / subtest (pipeline-stamped ``_source``). */
function RowSourceChip({ row, label }) {
  const cite = rowCite(row);
  if (!cite) return null;
  return <CitationChip cite={cite} label={label} size="xs" />;
}

/** Subtest / subdomain rows keyed by name, to find each one's ``_source``. */
function nestedRowsByName(row) {
  const out = new Map();
  ['subtests', 'subdomains', 'scores'].forEach((k) => {
    (Array.isArray(row?.[k]) ? row[k] : []).forEach((s) => {
      const name = String(s?.name || s?.measure || s?.label || '').trim().toLowerCase();
      if (name && s?._source && !out.has(name)) out.set(name, s);
    });
  });
  return out;
}

const INPUT_CLS = 'w-full px-3 py-2 rounded-lg bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors';
const COMPACT_INPUT_CLS = 'w-full px-2 py-1 rounded-md bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors';

function CheckNaValue({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center text-emerald-600 dark:text-emerald-400" title="Checked">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </span>
    );
  }
  return <span className="text-sm text-gray-400 dark:text-slate-500">N/A</span>;
}

function formatKey(key) {
  if (key === 'accommodations_table1') return 'Accommodations Table';
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatSourcePages(pages) {
  if (pages == null) return '';
  const list = Array.isArray(pages) ? pages : [pages];
  const nums = [...new Set(list.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n > 0))]
    .sort((a, b) => a - b);
  if (!nums.length) return '';
  return `p. ${nums.join(', ')}`;
}

/** Normalize page cite(s) from a field_source_pages / evidence-span value. */
function normalizePageList(raw) {
  if (raw == null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  return [...new Set(
    list.flatMap((item) => {
      if (item != null && typeof item === 'object' && !Array.isArray(item)) {
        const p = Number(item.page);
        return Number.isInteger(p) && p > 0 ? [p] : [];
      }
      const n = Number(item);
      return Number.isInteger(n) && n > 0 ? [n] : [];
    }),
  )].sort((a, b) => a - b);
}

/** Retrieval dumps often list dozens of pages; prefer evidence-span cites instead. */
function pagesForField(fieldKey, fieldSourcePages, evidenceSpans) {
  const cap = (
    fieldKey === 'goals' || fieldKey === 'goals_tables' || fieldKey === 'annual_goal'
  ) ? 12 : 4;
  const fromField = normalizePageList(fieldSourcePages?.[fieldKey]);
  if (fromField.length && fromField.length <= cap) return fromField;
  const span = evidenceSpans?.[fieldKey];
  const fromSpan = normalizePageList(span);
  if (fromSpan.length) return fromSpan;
  // Inflated field pages are not useful as UI cites — hide them.
  if (fromField.length > cap) return [];
  return fromField;
}

function citeForField(fieldKey, fieldSourcePages, evidenceSpans, fallbackKeys = []) {
  const keys = [fieldKey, ...(Array.isArray(fallbackKeys) ? fallbackKeys : [])];
  for (const key of keys) {
    const pages = pagesForField(key, fieldSourcePages, evidenceSpans);
    const raw = evidenceSpans?.[key];
    const spanObj = Array.isArray(raw) ? raw[0] : raw;
    if (pages.length || (spanObj && typeof spanObj === 'object')) {
      return {
        pages,
        fileId: spanObj?.student_file_id ?? spanObj?.file_id ?? null,
        fileName: spanObj?.file_name ?? null,
        quote: spanObj?.quote ?? null,
      };
    }
  }
  return { pages: [], fileId: null, fileName: null, quote: null };
}

function FieldLabelWithPages({ label, pages, onOpenFileAtPage, fileId, fileName, quote }) {
  const list = Array.isArray(pages)
    ? pages.map((n) => Number(n)).filter((n) => Number.isInteger(n) && n > 0)
    : pages != null && Number.isInteger(Number(pages)) && Number(pages) > 0
      ? [Number(pages)]
      : [];
  const cite = formatSourcePages(pages);
  const openable = Boolean(onOpenFileAtPage && list.length);
  return (
    <>
      {label}
      {cite ? (
        openable ? (
          <button
            type="button"
            className="block font-normal text-[10px] text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 mt-0.5 underline-offset-2 hover:underline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenFileAtPage(fileId, list[0], fileName, quote);
            }}
            title={quote ? `Open source page ${list[0]} — ${String(quote).slice(0, 80)}` : `Open source page ${list[0]}`}
          >
            {cite}
          </button>
        ) : (
          <span className="block font-normal text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">{cite}</span>
        )
      ) : null}
    </>
  );
}

function BoolToggle({ value, onChange, nullable = true }) {
  const cycle = () => {
    if (nullable) {
      if (value === true) onChange(false);
      else if (value === false) onChange(null);
      else onChange(true);
    } else {
      onChange(!value);
    }
  };
  const label = value === true ? 'Yes' : value === false ? 'No' : '—';
  const active = value === true ? 'bg-green-500 justify-end' : value === false ? 'bg-red-500 justify-start' : 'bg-gray-300 dark:bg-slate-600 justify-start';
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={cycle} className={`w-8 h-5 rounded-full flex-shrink-0 flex items-center transition-colors ${active}`}>
        <span className="w-3.5 h-3.5 bg-white rounded-full mx-0.5 shadow-sm" />
      </button>
      <span className="text-xs text-gray-500 dark:text-slate-400">{label}</span>
    </div>
  );
}

function StringListEditor({ items, onChange, placeholder, compact = false }) {
  const list = Array.isArray(items) ? items : [];
  const inputCls = compact ? COMPACT_INPUT_CLS : INPUT_CLS;
  const add = () => onChange([...list, '']);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(list.map((v, idx) => (idx === i ? val : v)));

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      {list.map((item, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <input type="text" value={item ?? ''} onChange={(e) => update(i, e.target.value)} placeholder={placeholder} className={inputCls} />
          <button type="button" onClick={() => remove(i)} className="p-1 rounded text-gray-400 hover:text-red-500 flex-shrink-0">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
        Add
      </button>
    </div>
  );
}

function emptyItemFromFields(itemFields) {
  const item = {};
  for (const f of itemFields || []) {
    if (f.type === 'boolean') item[f.key] = null;
    else if (f.type === 'string_list' || f.type === 'object_list') item[f.key] = [];
    else if (f.type === 'object') item[f.key] = {};
    else item[f.key] = '';
  }
  return item;
}

function ObjectListEditor({ items, onChange, fieldDef }) {
  const list = Array.isArray(items) ? items : [];
  const itemFields = fieldDef.itemFields || [];

  const add = () => onChange([...list, emptyItemFromFields(itemFields)]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateItem = (i, key, val) => onChange(list.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  return (
    <div className="space-y-3">
      {list.map((item, i) => (
        <div key={i} className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700 dark:text-slate-300">
              {objectListItemTitle(item, fieldDef, i)}
            </span>
            <button type="button" onClick={() => remove(i)} className="text-xs text-red-500 hover:text-red-600 font-medium">Remove</button>
          </div>
          {itemFields.map((sub) => (
            <FieldRow key={sub.key} fieldDef={sub} value={item?.[sub.key]} onChange={(val) => updateItem(i, sub.key, val)} compact />
          ))}
        </div>
      ))}
      <button type="button" onClick={add} className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
        Add {fieldDef.itemLabel || 'item'}
      </button>
    </div>
  );
}

function ObjectEditor({ value, onChange, fieldDef }) {
  const unwrapped = unwrapSingleObject(value);
  const obj = unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped) ? unwrapped : {};
  const update = (key, val) => {
    const existingKey = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase()) || key;
    const nextObj = { ...obj, [key]: val };
    if (existingKey !== key) delete nextObj[existingKey];
    onChange(nextObj);
  };

  const fields = fieldDef.fields || [];
  const useInlineGrid = fields.length > 0 && fields.length <= 4 && !fields.some((f) => f.type === 'text');

  if (useInlineGrid) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5">
        {fields.map((sub) => (
          <div key={sub.key} className="flex items-center gap-2 min-w-0">
            {sub.label ? (
              <label className="text-[11px] font-medium text-gray-500 dark:text-slate-400 w-24 shrink-0 truncate" title={sub.label}>
                {sub.label}
              </label>
            ) : null}
            <div className="flex-1 min-w-0">
              <FieldInput
                fieldDef={sub}
                value={getObjectFieldValue(obj, sub.key)}
                onChange={(val) => update(sub.key, val)}
                compact
              />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/40 p-2 space-y-1.5">
      {fields.map((sub) => (
        <FieldRow
          key={sub.key}
          fieldDef={sub}
          value={getObjectFieldValue(obj, sub.key)}
          onChange={(val) => update(sub.key, val)}
          compact
        />
      ))}
    </div>
  );
}

const DIBELS_MEASURE_NAMES = [
  'composite', 'fsf', 'lnf', 'psf', 'nwf', 'cls', 'wwr',
  'dorf', 'fluency', 'accuracy', 'retell', 'daze', 'maze',
];

/** A "measure" that is pipe-joined or names 2+ measures is a captured header row,
 *  not real data — drop it (and empty/valueless rows) so the table stays clean. */
function isGarbageDibelsRow(row) {
  if (!row || typeof row !== 'object') return true;
  const measure = String(row.measure || '').trim();
  if (!measure) return true;
  if (measure.includes('|')) return true;
  if (measure.split('/').filter(Boolean).length >= 3) return true;
  const hits = DIBELS_MEASURE_NAMES.filter((m) => measure.toLowerCase().includes(m)).length;
  if (hits >= 3) return true;
  const hasScore = row.score !== null && row.score !== undefined && String(row.score).trim() !== '';
  if (!hasScore) return true;
  // Prefer blank over incomplete invent (Year/Grade dashes in UI).
  const year = String(row.school_year || '').trim();
  const period = String(row.period || '').trim();
  if (!period || !/\d{4}/.test(year)) return true;
  const num = Number(String(row.score).replace('%', ''));
  if (!Number.isFinite(num)) return true;
  if (/accuracy/i.test(measure) && (num < 0 || num > 100)) return true;
  return false;
}

function scrubDibelsBenchmark(row) {
  const status = String(row.benchmark_status || '').toLowerCase();
  if (!status) return row;
  const num = Number(String(row.score).replace('%', ''));
  if (!Number.isFinite(num)) return { ...row, benchmark_status: null };
  if (
    (status.includes('at_benchmark') || status.includes('above'))
    && num <= 10
    && !/accuracy/i.test(String(row.measure || ''))
  ) {
    return { ...row, benchmark_status: null };
  }
  return row;
}

function scrubDibelsAdmin(admin) {
  const text = String(admin || '').trim();
  if (!text) return null;
  if (/procedural\s+safeguards|questions\s+regarding|contact\s+the\s+principal/i.test(text)) {
    return null;
  }
  if (text.length > 80) return null;
  return text;
}

/** Drop (grade, year, period) groups that are entirely zero — an un-assessed / future
 *  period the chart baselined at 0 (e.g. the whole "EOY 2021-2022" block of 0s). */
function dropUnassessedZeroGroups(scores) {
  const groups = new Map();
  scores.forEach((r) => {
    const key = `${r.grade_level || ''}|${r.school_year || ''}|${r.period || ''}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(r);
  });
  const kept = [];
  groups.forEach((rows) => {
    const nums = rows
      .map((r) => Number(r.score))
      .filter((n) => Number.isFinite(n));
    if (nums.length >= 2 && nums.every((n) => n === 0)) return;
    kept.push(...rows);
  });
  return kept;
}

function DibelsTableEditor({ value }) {
  const reports = Array.isArray(value) ? value : [];
  if (!reports.length) {
    return <p className="text-sm text-gray-400 dark:text-slate-500 italic">No DIBELS data extracted.</p>;
  }

  const benchClass = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('well_below')) return 'text-red-700 dark:text-red-400 font-medium';
    if (s.includes('below')) return 'text-amber-700 dark:text-amber-400 font-medium';
    if (s.includes('above')) return 'text-green-700 dark:text-green-400 font-medium';
    return 'text-slate-600 dark:text-slate-400';
  };

  return (
    <div className="space-y-4">
      {reports.map((report, ri) => {
        const scores = dropUnassessedZeroGroups(
          (Array.isArray(report?.scores) ? report.scores : [])
            .map((r) => {
              const copy = { ...r };
              if (!String(copy.school_year || '').trim()) copy.school_year = report.school_year;
              if (copy.grade_level == null || copy.grade_level === '') copy.grade_level = report.grade_level;
              if (!copy.period) copy.period = report.period;
              return copy;
            })
            .filter((r) => !isGarbageDibelsRow(r))
            .map((r) => scrubDibelsBenchmark(r))
        );
        const admin = scrubDibelsAdmin(report.administrator);
        const header = [
          report.tool || 'DIBELS Next',
          report.grade_level ? `Grade ${report.grade_level}` : null,
          report.school_year,
          admin ? `Admin: ${admin}` : null,
        ].filter(Boolean).join(' · ');
        if (!scores.length) {
          return (
            <p key={ri} className="text-sm text-gray-400 dark:text-slate-500 italic">
              No verified DIBELS rows for this report.
            </p>
          );
        }
        return (
          <div key={ri} className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {header && (
              <div className="px-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 text-sm font-semibold text-slate-700 dark:text-slate-300">
                {header}
              </div>
            )}
            {report.summary && scores.length === 0 && (
              <p className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 leading-relaxed">
                {report.summary}
              </p>
            )}
            {scores.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40">
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Measure</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Grade</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Period</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Year</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Score</th>
                      <th className="px-4 py-2.5 font-semibold whitespace-nowrap">Benchmark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.map((row, si) => (
                      <tr key={si} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                        <td className="px-4 py-2 text-slate-800 dark:text-slate-200 whitespace-nowrap">{row.measure || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.grade_level || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.period || '—'}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{row.school_year || '—'}</td>
                        <td className="px-4 py-2 font-semibold whitespace-nowrap">{row.score ?? '—'}</td>
                        <td className={`px-4 py-2 whitespace-nowrap ${benchClass(row.benchmark_status)}`}>
                          {(row.benchmark_status || '—').replace(/_/g, ' ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="px-4 py-3 text-sm text-gray-400 italic">No score rows.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PresentLevelsTable({ value, onChange, compact = false }) {
  const rows = Array.isArray(value) ? value.filter((r) => r && typeof r === 'object') : [];
  if (!rows.length) {
    return <span className="text-gray-400 dark:text-slate-500">—</span>;
  }
  const cell = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} text-gray-700 dark:text-slate-200 align-top`;
  const head = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400`;
  const editing = typeof onChange === 'function';
  const patchRow = (i, key, val) => {
    if (!editing) return;
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val || null } : r)));
  };
  const cols = [
    ['area', 'Area', false],
    ['strengths', 'Strengths / Current Performance', true],
    ['needs', 'Needs / Skill Gaps', true],
    ['data_evidence', 'Data / Evidence', true],
    ['impact_statement', 'Impact Statement', true],
    ['corresponding_goal', 'Corresponding Goal', false],
    ['service_time', 'Service Time / Frequency', false],
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse">
        <thead>
          <tr>
            {cols.map(([, label]) => (
              <th key={label} className={head}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.area || i}>
              {cols.map(([key, , multiline]) => (
                <td key={key} className={`${cell}${key === 'area' || key === 'corresponding_goal' ? ' font-medium whitespace-nowrap' : ''}${key === 'service_time' ? ' whitespace-nowrap' : ''}`}>
                  {editing ? (
                    multiline ? (
                      <textarea
                        value={row[key] || ''}
                        onChange={(e) => patchRow(i, key, e.target.value)}
                        rows={2}
                        className={`${INPUT_CLS} resize-y ${compact ? 'text-[11px] py-1' : ''}`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={row[key] || ''}
                        onChange={(e) => patchRow(i, key, e.target.value)}
                        className={`${INPUT_CLS} ${compact ? 'text-[11px] py-1' : ''}`}
                      />
                    )
                  ) : ((row[key] && String(row[key]).trim()) ? row[key] : '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PROGRESS_MEASURED_OPTIONS = [
  'Curriculum-based Assessment',
  'Test Scores',
  'Checklist',
  'Observations',
  'Other',
];
const PARENT_INFORMED_OPTIONS = [
  'Weekly',
  'Every Trimester',
  'Quarterly',
  'Concurrent with the issuance of report cards',
  'Other',
];

function tagAnnualGoal(row) {
  const text = String(row?.annual_goal || '').trim();
  if (!text) return null;
  const parts = [
    [row.condition, 'Condition'],
    [row.skill_behavior, 'Skill/Behavior'],
    [row.criteria, 'Criteria'],
  ].filter(([phrase]) => phrase && String(phrase).trim());
  if (!parts.length) return text;
  const nodes = [];
  let cursor = 0;
  const lower = text.toLowerCase();
  const used = new Set();
  for (const [phrase, tag] of parts) {
    const needle = String(phrase).trim();
    const at = lower.indexOf(needle.toLowerCase(), cursor);
    if (at < 0 || used.has(tag)) continue;
    used.add(tag);
    if (at > cursor) nodes.push(text.slice(cursor, at));
    nodes.push(text.slice(at, at + needle.length));
    nodes.push(
      <span key={tag} className="ml-1 font-semibold text-red-600 dark:text-red-400">({tag})</span>,
    );
    cursor = at + needle.length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  for (const [phrase, tag] of parts) {
    if (used.has(tag)) continue;
    nodes.push(' ');
    nodes.push(String(phrase).trim());
    nodes.push(
      <span key={tag} className="ml-1 font-semibold text-red-600 dark:text-red-400">({tag})</span>,
    );
  }
  return nodes;
}

function CheckboxColumn({ options, checked, other, compact, onToggle }) {
  const selected = Array.isArray(checked)
    ? checked.map((v) => String(v).trim().toLowerCase())
    : (checked ? [String(checked).trim().toLowerCase()] : []);
  const textCls = compact ? 'text-[11px]' : 'text-xs';
  return (
    <ul className={`space-y-0.5 ${textCls} text-gray-700 dark:text-slate-200`}>
      {options.map((opt) => {
        const on = selected.includes(opt.toLowerCase());
        const specify = opt === 'Other' && on && other ? ` — ${other}` : '';
        return (
          <li key={opt} className="flex items-start gap-1.5">
            {onToggle ? (
              <button
                type="button"
                onClick={() => onToggle(opt, !on)}
                className={`${on ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-300 dark:text-slate-600'} hover:opacity-80`}
                aria-pressed={on}
              >
                {on ? '☑' : '☐'}
              </button>
            ) : (
              <span className={on ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-300 dark:text-slate-600'}>
                {on ? '☑' : '☐'}
              </span>
            )}
            <span>{opt}{specify}</span>
          </li>
        );
      })}
    </ul>
  );
}

function emptyGoalTable() {
  return {
    goal_area: null,
    rows: [
      { label: 'Utah State Grade Level Standard or Essential Element', value: null },
      { label: 'Annual Goal', annual_goal: null, condition: null, skill_behavior: null, criteria: null },
      { label: 'Benchmarks/Short-term Objectives', value: [] },
      { label: 'How Progress Will Be Measured', checked: [], options: PROGRESS_MEASURED_OPTIONS, other: null },
      { label: 'When Parent Will Be Informed of Progress', checked: null, options: PARENT_INFORMED_OPTIONS, other: null },
    ],
  };
}

function GoalsClientTables({ value, onChange, compact = false }) {
  const tables = Array.isArray(value) && value.length ? value.filter((t) => t && typeof t === 'object') : [emptyGoalTable()];
  const cell = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} text-gray-700 dark:text-slate-200 align-top`;
  const head = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400`;
  const dash = (v) => (v && String(v).trim() ? v : '—');
  const editing = typeof onChange === 'function';

  const patchTable = (ti, patch) => {
    if (!editing) return;
    onChange(tables.map((t, i) => (i === ti ? { ...t, ...patch } : t)));
  };

  const patchRow = (ti, label, patch) => {
    if (!editing) return;
    onChange(tables.map((t, i) => {
      if (i !== ti) return t;
      const rows = Array.isArray(t.rows) ? t.rows.map((r) => ({ ...r })) : emptyGoalTable().rows.map((r) => ({ ...r }));
      const idx = rows.findIndex((r) => r.label === label);
      if (idx >= 0) rows[idx] = { ...rows[idx], ...patch };
      return { ...t, rows };
    }));
  };

  const toggleProgressOption = (ti, label, opt, on) => {
    const table = tables[ti] || {};
    const rows = Array.isArray(table.rows) ? table.rows : [];
    const row = rows.find((r) => r.label === label) || {};
    const current = Array.isArray(row.checked)
      ? [...row.checked]
      : (row.checked ? [row.checked] : []);
    const lower = current.map((v) => String(v).trim().toLowerCase());
    const optLower = opt.toLowerCase();
    let next;
    if (on) {
      next = lower.includes(optLower) ? current : [...current, opt];
    } else {
      next = current.filter((v) => String(v).trim().toLowerCase() !== optLower);
    }
    patchRow(ti, label, { checked: next.length === 1 ? next[0] : next });
  };

  return (
    <div className="space-y-4">
      {tables.map((table, ti) => {
        const rows = Array.isArray(table.rows) ? table.rows : emptyGoalTable().rows;
        const byLabel = Object.fromEntries(rows.map((r) => [r.label, r]));
        const standard = byLabel['Utah State Grade Level Standard or Essential Element'] || {};
        const annual = byLabel['Annual Goal'] || {};
        const benches = byLabel['Benchmarks/Short-term Objectives'] || {};
        const progress = byLabel['How Progress Will Be Measured'] || {};
        const informed = byLabel['When Parent Will Be Informed of Progress'] || {};
        const benchList = Array.isArray(benches.value) ? benches.value.filter(Boolean) : (benches.value ? [benches.value] : []);
        const annualTagged = tagAnnualGoal(annual);
        return (
          <div key={table.goal_area || ti}>
            {editing ? (
              <input
                type="text"
                value={table.goal_area || ''}
                onChange={(e) => patchTable(ti, { goal_area: e.target.value || null })}
                placeholder="Goal area"
                className={`${INPUT_CLS} mb-1 ${compact ? 'text-[11px] py-1' : ''}`}
              />
            ) : table.goal_area ? (
              <p className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold text-gray-600 dark:text-slate-300 mb-1`}>
                {table.goal_area}
              </p>
            ) : null}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse">
                <thead>
                  <tr>
                    <th className={`${head} w-[38%]`}>Special Education Goal</th>
                    <th className={head}>Goal Information</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={`${cell} font-medium`}>
                      Utah State Grade Level Standard or Essential Element
                      <div className="font-normal text-red-600 dark:text-red-400 mt-0.5">students taking DLM / alternate</div>
                    </td>
                    <td className={cell}>
                      {editing ? (
                        <textarea
                          value={standard.value || ''}
                          onChange={(e) => patchRow(ti, 'Utah State Grade Level Standard or Essential Element', { value: e.target.value || null })}
                          rows={2}
                          className={`${INPUT_CLS} resize-y ${compact ? 'text-[11px] py-1' : ''}`}
                        />
                      ) : dash(standard.value)}
                    </td>
                  </tr>
                  <tr>
                    <td className={`${cell} font-medium`}>Annual Goal</td>
                    <td className={cell}>
                      {editing ? (
                        <div className="space-y-1">
                          <textarea
                            value={annual.annual_goal || ''}
                            onChange={(e) => patchRow(ti, 'Annual Goal', { annual_goal: e.target.value || null })}
                            rows={3}
                            className={`${INPUT_CLS} resize-y ${compact ? 'text-[11px] py-1' : ''}`}
                            placeholder="Annual goal text"
                          />
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
                            {['condition', 'skill_behavior', 'criteria'].map((k) => (
                              <input
                                key={k}
                                type="text"
                                value={annual[k] || ''}
                                onChange={(e) => patchRow(ti, 'Annual Goal', { [k]: e.target.value || null })}
                                placeholder={k.replace(/_/g, ' ')}
                                className={`${INPUT_CLS} ${compact ? 'text-[11px] py-1' : ''}`}
                              />
                            ))}
                          </div>
                        </div>
                      ) : (annualTagged || '—')}
                    </td>
                  </tr>
                  <tr>
                    <td className={`${cell} font-medium`}>
                      Benchmarks / Short-term Objectives
                      <div className="font-normal text-red-600 dark:text-red-400 mt-0.5">optional unless DLM</div>
                    </td>
                    <td className={cell}>
                      {editing ? (
                        <textarea
                          value={benchList.join('\n')}
                          onChange={(e) => patchRow(ti, 'Benchmarks/Short-term Objectives', {
                            value: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean),
                          })}
                          rows={3}
                          placeholder="One objective per line"
                          className={`${INPUT_CLS} resize-y font-mono ${compact ? 'text-[11px] py-1' : ''}`}
                        />
                      ) : benchList.length ? (
                        <ol className="list-decimal list-inside space-y-0.5">
                          {benchList.map((b, bi) => <li key={bi}>{b}</li>)}
                        </ol>
                      ) : '—'}
                    </td>
                  </tr>
                  <tr>
                    <td className={`${cell} font-medium`}>How Progress Will Be Measured</td>
                    <td className={cell}>
                      <CheckboxColumn
                        options={progress.options || PROGRESS_MEASURED_OPTIONS}
                        checked={progress.checked}
                        other={progress.other}
                        compact={compact}
                        onToggle={editing ? (opt, on) => toggleProgressOption(ti, 'How Progress Will Be Measured', opt, on) : undefined}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td className={`${cell} font-medium`}>When Parent Will Be Informed of Progress</td>
                    <td className={cell}>
                      <CheckboxColumn
                        options={informed.options || PARENT_INFORMED_OPTIONS}
                        checked={informed.checked}
                        other={informed.other}
                        compact={compact}
                        onToggle={editing ? (opt, on) => toggleProgressOption(ti, 'When Parent Will Be Informed of Progress', opt, on) : undefined}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AssessmentDataTable({ value, onChange, compact = false }) {
  const rows = Array.isArray(value) ? value.filter((r) => r && typeof r === 'object') : [];
  if (!rows.length) {
    return <span className="text-gray-400 dark:text-slate-500">—</span>;
  }
  const cell = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} text-gray-700 dark:text-slate-200`;
  const head = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;
  const editing = typeof onChange === 'function';
  const patchRow = (i, patch) => {
    if (!editing) return;
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const mark = (on) => (on ? (
    <span className="text-emerald-600 dark:text-emerald-400 font-semibold" title="Marked">✓</span>
  ) : (
    <span className="text-gray-300 dark:text-slate-600">—</span>
  ));
  const boolCell = (row, i, key) => (
    editing ? (
      <div className="flex justify-center">
        <BoolToggle
          value={row[key]}
          onChange={(v) => patchRow(i, { [key]: v })}
          nullable
        />
      </div>
    ) : (
      mark(row[key])
    )
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse">
        <thead>
          <tr>
            <th className={head}>Type</th>
            <th className={head}>Required by State</th>
            <th className={head}>Marked on Data Review</th>
            <th className={head}>Marked on PWN</th>
            <th className={head}>Pulled Forward</th>
            <th className={head}>Date Testing Completed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.type || i}>
              <td className={`${cell} font-medium`}>{row.type || '—'}</td>
              <td className={`${cell} text-center`}>{boolCell(row, i, 'required_by_state')}</td>
              <td className={`${cell} text-center`}>{boolCell(row, i, 'marked_on_data_review')}</td>
              <td className={`${cell} text-center`}>{boolCell(row, i, 'marked_on_pwn')}</td>
              <td className={`${cell} text-center`}>{boolCell(row, i, 'pulled_forward')}</td>
              <td className={`${cell} whitespace-nowrap`}>
                {editing ? (
                  <DatePicker
                    value={row.date_testing_completed || ''}
                    onChange={(v) => patchRow(i, { date_testing_completed: v || null })}
                    className={`${INPUT_CLS} ${compact ? 'text-[11px] py-1' : ''}`}
                    placeholder="MM/DD/YYYY"
                    aria-label={`Date for ${row.type || 'assessment'}`}
                  />
                ) : (row.date_testing_completed || '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JsonFieldEditor({ value, onChange }) {
  const [text, setText] = useState(() => JSON.stringify(value ?? {}, null, 2));
  const [error, setError] = useState('');

  const apply = () => {
    try {
      onChange(JSON.parse(text));
      setError('');
    } catch {
      setError('Invalid JSON');
    }
  };

  return (
    <div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} onBlur={apply} rows={4} className={`${INPUT_CLS} font-mono text-xs resize-y`} />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

const CHART_TYPE_OPTIONS = ['profile', 'line', 'bar', 'scatter', 'table', 'histogram', 'unknown'];

function chartSeriesRows(chart) {
  const out = [];
  for (const s of chart?.series || []) {
    if (!s || typeof s !== 'object') continue;
    const name = String(s.name || 'Series').trim();
    for (const p of s.data_points || []) {
      if (!p || typeof p !== 'object') continue;
      if (p.y == null && !String(p.x || '').trim()) continue;
      out.push({
        series: name,
        x: p.x != null ? String(p.x) : '',
        y: p.y != null ? String(p.y) : '',
        label: p.label != null ? String(p.label) : '',
      });
    }
  }
  return out;
}

function ReadOnlyChartProfiles({ value, compact = false }) {
  const list = normalizeChartProfilesList(value);
  if (!list.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  const cell = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-0.5 text-[11px]' : 'py-1 text-xs'} text-gray-700 dark:text-slate-200`;
  const head = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-0.5 text-[11px]' : 'py-1 text-xs'} bg-gray-50 dark:bg-slate-900 font-semibold text-gray-500 dark:text-slate-400 text-left`;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {list.map((chart, i) => {
        const points = chartSeriesRows(chart);
        const title = chart.title || chart.name || chart.chart_type || `Chart ${i + 1}`;
        return (
          <article key={i} className="rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden">
            <header className={`px-2 ${compact ? 'py-1' : 'py-1.5'} bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 text-xs font-semibold text-gray-800 dark:text-slate-100`}>
              {title}
            </header>
            <table className="w-full border-collapse">
              <tbody>
                {chart.chart_type ? (
                  <tr><td className={`${cell} w-24 text-gray-500`}>Type</td><td className={cell}>{chart.chart_type}</td></tr>
                ) : null}
                {chart.x_axis_label ? (
                  <tr><td className={`${cell} text-gray-500`}>X Axis</td><td className={cell}>{chart.x_axis_label}</td></tr>
                ) : null}
                {chart.y_axis_label ? (
                  <tr><td className={`${cell} text-gray-500`}>Y Axis</td><td className={cell}>{chart.y_axis_label}</td></tr>
                ) : null}
                {chart.summary || chart.description ? (
                  <tr><td className={`${cell} align-top text-gray-500`}>Summary</td><td className={`${cell} whitespace-pre-wrap`}>{chart.summary || chart.description}</td></tr>
                ) : null}
              </tbody>
            </table>
            {points.length > 0 && (
              <div className="overflow-x-auto border-t border-gray-100 dark:border-slate-700">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={head}>Series</th>
                      <th className={head}>X</th>
                      <th className={head}>Y</th>
                      <th className={head}>Label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {points.map((p, j) => (
                      <tr key={j}>
                        <td className={cell}>{p.series}</td>
                        <td className={cell}>{p.x || '—'}</td>
                        <td className={cell}>{p.y || '—'}</td>
                        <td className={cell}>{p.label || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function ChartProfilesEditor({ value, onChange }) {
  const list = normalizeChartProfilesList(value);
  const patchList = (next) => onChange(next.length ? next : null);
  const patchChart = (i, patch) => patchList(list.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeChart = (i) => patchList(list.filter((_, idx) => idx !== i));
  const addChart = () => patchList([...list, { title: '', chart_type: 'profile', summary: '' }]);
  const cell = 'border border-gray-200 dark:border-slate-600 px-1.5 py-0.5 text-[11px]';
  const head = 'border border-gray-200 dark:border-slate-600 px-1.5 py-0.5 text-[11px] bg-gray-50 dark:bg-slate-900 font-semibold text-gray-500 dark:text-slate-400 text-left';

  if (!list.length) {
    return (
      <button type="button" onClick={addChart} className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
        + Add chart profile
      </button>
    );
  }

  return (
    <div className="space-y-2">
      {list.map((chart, i) => (
        <article key={i} className="rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden">
          <header className="px-2 py-1.5 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 flex flex-wrap gap-1.5 items-center">
            <input
              type="text"
              value={chart.title || chart.name || ''}
              onChange={(e) => patchChart(i, { title: e.target.value })}
              placeholder="Chart title"
              className={`${COMPACT_INPUT_CLS} flex-1 min-w-[120px]`}
            />
            <select
              value={chart.chart_type || 'profile'}
              onChange={(e) => patchChart(i, { chart_type: e.target.value })}
              className={`${COMPACT_INPUT_CLS} w-24`}
            >
              {CHART_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button type="button" onClick={() => removeChart(i)} className="text-[10px] text-red-500 ml-auto">Remove</button>
          </header>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 p-2">
            <input
              type="text"
              value={chart.x_axis_label || ''}
              onChange={(e) => patchChart(i, { x_axis_label: e.target.value })}
              placeholder="X axis label"
              className={COMPACT_INPUT_CLS}
            />
            <input
              type="text"
              value={chart.y_axis_label || ''}
              onChange={(e) => patchChart(i, { y_axis_label: e.target.value })}
              placeholder="Y axis label"
              className={COMPACT_INPUT_CLS}
            />
          </div>
          <textarea
            value={chart.summary || chart.description || ''}
            onChange={(e) => patchChart(i, { summary: e.target.value, description: e.target.value })}
            placeholder="Summary — trend, peaks, deficits, readable values from the graph"
            rows={2}
            className={`${COMPACT_INPUT_CLS} mx-2 mb-2 resize-y`}
          />
          {chartSeriesRows(chart).length > 0 && (
            <div className="overflow-x-auto border-t border-gray-100 dark:border-slate-700">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={head}>Series</th>
                    <th className={head}>X</th>
                    <th className={head}>Y</th>
                  </tr>
                </thead>
                <tbody>
                  {chartSeriesRows(chart).map((p, j) => (
                    <tr key={j}>
                      <td className={cell}>{p.series}</td>
                      <td className={cell}>{p.x || '—'}</td>
                      <td className={cell}>{p.y || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      ))}
      <button type="button" onClick={addChart} className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
        + Add chart profile
      </button>
    </div>
  );
}

function FieldInput({ fieldDef, value, onChange, compact = false }) {
  const { type, options, placeholder } = fieldDef;
  const inputCls = compact ? COMPACT_INPUT_CLS : INPUT_CLS;

  switch (type) {
    case 'boolean':
      return <BoolToggle value={value} onChange={onChange} />;
    case 'text':
      return (
        <textarea
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          rows={compact ? 2 : 3}
          placeholder={placeholder}
          className={`${inputCls} resize-y`}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          className={inputCls}
        />
      );
    case 'select': {
      const opts = (Array.isArray(options) ? options : []).filter(Boolean);
      return (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} className={inputCls}>
          <option value="">—</option>
          {opts.map((opt) => (
            <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
          ))}
        </select>
      );
    }
    case 'string_list':
      return <StringListEditor items={value} onChange={onChange} placeholder={placeholder || 'Item...'} compact={compact} />;
    case 'object_list':
      if (ASSESSMENT_TABLE_KEYS.has(fieldDef.key)) {
        if (fieldDef.key === 'academic_standardized') {
          return (
            <AssessmentBatteryEditor
              items={value}
              onChange={onChange}
              scoreHeader={SCORE_COL_STANDARD}
              measureHeader="Subtest"
              filterItems={filterAcademicTableRows}
              itemLabel={fieldDef.itemLabel || 'Test'}
              showExaminer={false}
            />
          );
        }
        if (fieldDef.key === 'classroom_assessments') {
          return (
            <AssessmentBatteryEditor
              items={value}
              onChange={onChange}
              scoreHeader="Score"
              measureHeader="Measure"
              itemLabel={fieldDef.itemLabel || 'Assessment'}
              showExaminer={false}
            />
          );
        }
        const isBehavioral = fieldDef.key === 'behavioral_assessments';
        const isAdaptive = fieldDef.key === 'adaptive_assessments';
        return (
          <AssessmentBatteryEditor
            items={value}
            onChange={onChange}
            scoreHeader={isBehavioral ? 'T' : SCORE_COL_STANDARD}
            measureHeader={isBehavioral ? 'Scale' : isAdaptive ? 'Domain' : 'Measure'}
            itemLabel={fieldDef.itemLabel || 'Survey'}
            showExaminer
            showRespondent={isAdaptive}
          />
        );
      }
      return <CompactObjectListEditor items={value} onChange={onChange} fieldDef={fieldDef} />;
    case 'object':
      if (fieldDef.key === 'cognitive_assessment') {
        return <CognitiveAssessmentEditor value={value} onChange={onChange} />;
      }
      return <ObjectEditor value={value} onChange={onChange} fieldDef={fieldDef} />;
    case 'json':
      if (fieldDef.key === 'chart_profiles') {
        return <ChartProfilesEditor value={value} onChange={onChange} />;
      }
      return <JsonFieldEditor value={value} onChange={onChange} />;
    case 'assessment_data_table':
      return <AssessmentDataTable value={value} onChange={onChange} />;
    case 'present_levels_table':
      return <PresentLevelsTable value={value} onChange={onChange} />;
    case 'goals_client_tables':
      return <GoalsClientTables value={value} onChange={onChange} />;
    case 'compliance_checklist':
      return (
        <ComplianceChecklistEditor
          items={value}
          onChange={onChange}
          fieldKey={fieldDef?.key}
          statuses={fieldDef?.statuses}
        />
      );
    case 'special_factors_grid':
      return <SpecialFactorsGridEditor value={value} onChange={onChange} />;
    case 'dibels_table':
      return <DibelsTableEditor value={value} />;
    case 'date':
      return (
        <DatePicker
          value={value ?? ''}
          onChange={onChange}
          className={inputCls}
          placeholder={placeholder || 'MM/DD/YYYY'}
          aria-label={fieldDef?.label || 'Date'}
        />
      );
    case 'string':
    default:
      return (
        <input
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputCls}
        />
      );
  }
}

function FieldRow({ fieldDef, value, onChange, compact = false, pages, onOpenFileAtPage, fileId, fileName, quote }) {
  const label = fieldDef.label || formatKey(fieldDef.key);
  const isBlock = ['text', 'string_list', 'object_list', 'object', 'json', 'dibels_table', 'assessment_data_table', 'present_levels_table', 'goals_client_tables', 'compliance_checklist', 'special_factors_grid'].includes(fieldDef.type);
  const pageProps = { pages, onOpenFileAtPage, fileId, fileName, quote };

  if (compact && isBlock) {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-gray-500 dark:text-slate-400">
          <FieldLabelWithPages label={label} {...pageProps} />
        </label>
        <FieldInput fieldDef={fieldDef} value={value} onChange={onChange} compact={compact} />
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
        <label className="text-xs font-medium text-gray-500 dark:text-slate-400 sm:w-1/3 flex-shrink-0">
          <FieldLabelWithPages label={label} {...pageProps} />
        </label>
        <div className="flex-1 min-w-0"><FieldInput fieldDef={fieldDef} value={value} onChange={onChange} compact={compact} /></div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
        <FieldLabelWithPages label={label} {...pageProps} />
      </label>
      <FieldInput fieldDef={fieldDef} value={value} onChange={onChange} compact={compact} />
    </div>
  );
}

function getObjectFieldValue(obj, key) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return undefined;
  if (obj[key] !== undefined) return obj[key];
  const match = Object.keys(obj).find((k) => k.toLowerCase() === String(key).toLowerCase());
  return match ? obj[match] : undefined;
}

function unwrapSingleObject(value) {
  if (Array.isArray(value) && value.length === 1 && typeof value[0] === 'object' && value[0] !== null) {
    return value[0];
  }
  return value;
}

function isEmptyValue(val) {
  if (val === null || val === undefined || val === '') return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true;
  return false;
}

function formatYesNo(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  const text = String(value ?? '').trim();
  if (/^(yes|true)$/i.test(text)) return 'Yes';
  if (/^(no|false)$/i.test(text)) return 'No';
  if (/^n\/?a$/i.test(text)) return 'N/A';
  return text || '—';
}

function compactFactorLabel(raw) {
  return String(raw || '').replace(/^Exclusionary\s*[—–-]\s*/i, '').trim();
}

function ReadOnlyYesNoPairs({ pairs, compact = false }) {
  const rows = Array.isArray(pairs) ? pairs.filter((p) => p && p.label) : [];
  if (!rows.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-6 ${compact ? 'gap-y-0.5' : 'gap-y-1'}`}>
      {rows.map((row) => (
        <div key={row.label} className={compact ? 'text-[11px]' : 'text-sm'}>
          <span className="text-gray-500 dark:text-slate-400">{row.label}:</span>{' '}
          <span className="font-medium text-gray-800 dark:text-slate-100">{formatYesNo(row.value)}</span>
        </div>
      ))}
    </div>
  );
}

function ReadOnlyTeamParticipationTable({ items, compact = false }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  const cell = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} text-gray-700 dark:text-slate-200`;
  const head = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400`;
  return (
    <div className="overflow-x-auto">
      <table className={`w-full border-collapse ${compact ? 'text-[11px]' : 'text-xs'}`}>
        <thead>
          <tr>
            <th className={head}>Required Team Member</th>
            <th className={head}>Name</th>
            <th className={head}>Participated?</th>
            <th className={head}>Excused?</th>
          </tr>
        </thead>
        <tbody>
          {list.map((row, i) => (
            <tr key={i}>
              <td className={cell}>{row?.role || '—'}</td>
              <td className={cell}>{row?.name || '—'}</td>
              <td className={cell}>{formatYesNo(row?.participated)}</td>
              <td className={cell}>{formatYesNo(row?.excused)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** ss + description (+ optional percentile) — show inline in read-only UI */
function isInlineScoreSubfields(subFields) {
  if (!Array.isArray(subFields) || subFields.length === 0) return false;
  const keys = subFields.map((f) => f.key);
  if (!keys.includes('ss') || !keys.includes('description')) return false;
  return keys.every((k) => ['ss', 'description', 'percentile'].includes(k));
}

function ReadOnlyInlineScoreValue({ obj, compact = false }) {
  const ss = getObjectFieldValue(obj, 'ss');
  const desc = getObjectFieldValue(obj, 'description');
  const pct = getObjectFieldValue(obj, 'percentile');
  if (isEmptyValue(ss) && isEmptyValue(desc) && isEmptyValue(pct)) {
    return <span className="text-gray-400 dark:text-slate-500">—</span>;
  }
  const textCls = compact ? 'text-[11px]' : 'text-sm';
  const scorePart = !isEmptyValue(ss) ? String(ss) : '';
  const descPart = !isEmptyValue(desc) ? String(desc) : '';
  const pctPart = !isEmptyValue(pct) ? String(pct) : '';
  return (
    <span className={`${textCls} text-gray-700 dark:text-slate-200`}>
      {scorePart && <span className="font-medium tabular-nums">{scorePart}</span>}
      {pctPart && (
        <span className="text-gray-500 dark:text-slate-400">
          {scorePart ? ' (' : ''}
          {pctPart}
          {!String(pctPart).includes('th') && !String(pctPart).includes('%') ? 'th' : ''}
          {scorePart ? ')' : ''}
        </span>
      )}
      {descPart && (
        <span className="text-gray-600 dark:text-slate-300">
          {(scorePart || pctPart) ? ' · ' : ''}
          {descPart}
        </span>
      )}
    </span>
  );
}

function pruneForSave(data, schema, sectionKey) {
  if (!schema) return data;
  const schemaKeys = new Set(schema.fields.map((f) => f.key));
  const result = {};

  for (const field of schema.fields) {
    const val = data[field.key];
    if (val === undefined) continue;
    let normalized = val;
    if (field.type === 'string_list' || field.type === 'object_list' || field.type === 'compliance_checklist'
      || field.type === 'assessment_data_table' || field.type === 'present_levels_table'
      || field.type === 'goals_client_tables' || field.type === 'dibels_table') {
      normalized = Array.isArray(val) ? val : [];
    } else if (field.type === 'object' || field.type === 'json' || field.type === 'special_factors_grid') {
      normalized = val && typeof val === 'object' ? val : (field.type === 'json' ? {} : {});
    } else if (field.type === 'number') {
      normalized = val === '' || val === null ? null : val;
    } else if (field.type === 'boolean') {
      normalized = val;
    } else {
      normalized = val === '' ? null : val;
    }
    if (!isEmptyValue(normalized)) {
      if (field.key === 'chart_profiles' && Array.isArray(normalized)) {
        normalized = normalizeChartProfilesList(normalized);
      }
      if (!isEmptyValue(normalized)) {
        result[field.key] = normalized;
      }
    }
  }

  for (const [key, val] of Object.entries(data)) {
    if (
      !schemaKeys.has(key)
      && !isHiddenExtractedKey(key)
      && !isRetiredExtraKey(sectionKey, key)
      && !isEmptyValue(val)
    ) {
      result[key] = val;
    }
  }

  return result;
}

function valueForField(data, field) {
  const primary = data[field.key];
  if (primary !== undefined && primary !== null && primary !== '') {
    return coerceFieldValue(field, primary);
  }
  if (Array.isArray(field.fallbackKeys)) {
    for (const k of field.fallbackKeys) {
      const fv = data[k];
      if (fv !== undefined && fv !== null && fv !== '') return coerceFieldValue(field, fv);
    }
  }
  if (primary !== undefined) return coerceFieldValue(field, primary);
  return defaultForDisplay(field.type);
}

function defaultForDisplay(type) {
  switch (type) {
    case 'string_list':
    case 'object_list':
    case 'compliance_checklist':
    case 'assessment_data_table':
    case 'present_levels_table':
    case 'goals_client_tables':
    case 'dibels_table':
      return [];
    case 'object':
    case 'json':
    case 'special_factors_grid':
      return {};
    case 'boolean':
    case 'number':
      return null;
    default:
      return '';
  }
}

function ReadOnlyObjectEditor({ value, fieldDef, compact = false }) {
  const unwrapped = unwrapSingleObject(value);
  const obj = unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped) ? unwrapped : {};
  const subFields = fieldDef?.fields || [];

  if (subFields.length && isInlineScoreSubfields(subFields)) {
    return <ReadOnlyInlineScoreValue obj={obj} compact={compact} />;
  }

  const primaryField = subFields.find((f) => f && f.display === 'primary');
  if (primaryField) {
    const primaryVal = getObjectFieldValue(obj, primaryField.key);
    const extra = subFields
      .filter((f) => f && f.display !== 'primary')
      .map((f) => [f, getObjectFieldValue(obj, f.key)])
      .filter(([, v]) => !isEmptyValue(v));
    const yesNo = (
      <span className={compact ? 'text-[11px] text-gray-700 dark:text-slate-200' : 'text-sm text-gray-700 dark:text-slate-200'}>
        {formatYesNo(primaryVal)}
      </span>
    );
    if (!extra.length) return yesNo;
    return (
      <div className="space-y-1">
        {yesNo}
        {extra.map(([f, v]) => (
          <div key={f.key} className={compact ? 'text-[11px]' : 'text-xs'}>
            <span className="text-gray-400 dark:text-slate-500 font-medium">{f.label}: </span>
            <ReadOnlyFieldValue field={f} value={v} compact={compact} />
          </div>
        ))}
      </div>
    );
  }

  const entries = subFields.length
    ? subFields.map((f) => [f, getObjectFieldValue(obj, f.key)]).filter(([, v]) => !isEmptyValue(v))
    : Object.entries(obj).filter(([, v]) => !isEmptyValue(v));

  if (entries.length === 0) return <span className="text-gray-400 dark:text-slate-500">—</span>;

  const booleanSubs = subFields.length > 0 && subFields.every((f) => f.type === 'boolean');
  if (booleanSubs || fieldDef?.key === 'exclusionary_factors') {
    const pairs = (subFields.length ? subFields : entries.map(([f]) => f)).map((f) => {
      const key = typeof f === 'object' ? f.key : f;
      const label = typeof f === 'object' ? (f.label || formatKey(key)) : formatKey(key);
      const value = typeof f === 'object' ? getObjectFieldValue(obj, f.key) : obj[key];
      return { label, value };
    });
    return <ReadOnlyYesNoPairs pairs={pairs} compact={compact} />;
  }

  if (compact && subFields.length) {
    return (
      <div className="rounded border border-gray-100 dark:border-slate-700/50 overflow-hidden">
        <table className="w-full border-collapse text-[11px]">
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
            {entries.map(([f, v]) => {
              const key = typeof f === 'object' ? f.key : f;
              const label = typeof f === 'object' ? (f.label || formatKey(key)) : formatKey(key);
              const field = typeof f === 'object' ? f : { key, type: 'string', label };
              return (
                <tr key={key}>
                  <td className="px-2 py-1 font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 w-[55%] align-top">{label}</td>
                  <td className="px-2 py-1 text-gray-700 dark:text-slate-200 align-top">
                    <ReadOnlyFieldValue field={field} value={v} compact={compact} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-700/50 p-3 space-y-2">
      {entries.map(([f, v]) => {
        const key = typeof f === 'object' ? f.key : f;
        const label = typeof f === 'object' ? (f.label || formatKey(key)) : formatKey(key);
        const field = typeof f === 'object' ? f : { key, type: 'string', label };
        return (
          <div key={key} className="text-xs">
            <div className="text-gray-400 dark:text-slate-500 font-medium mb-0.5">{label}</div>
            <ReadOnlyFieldValue field={field} value={v} compact={compact} />
          </div>
        );
      })}
    </div>
  );
}

function ReadOnlyTeamTable({ items, compact = false }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;

  const cellCls = compact
    ? 'border border-gray-200 dark:border-slate-600 px-2 py-1 text-gray-700 dark:text-slate-200'
    : 'border border-gray-200 dark:border-slate-600 px-2 py-1.5 text-gray-700 dark:text-slate-200';
  const headCls = compact
    ? 'border border-gray-200 dark:border-slate-600 px-2 py-1 bg-gray-50 dark:bg-slate-900 text-left font-medium text-gray-500 dark:text-slate-400'
    : 'border border-gray-200 dark:border-slate-600 px-2 py-1.5 bg-gray-50 dark:bg-slate-900 text-left font-medium text-gray-500 dark:text-slate-400';

  return (
    <div className="overflow-x-auto max-h-48">
      <table className={`w-full border-collapse ${compact ? 'text-[11px]' : 'text-xs'}`}>
        <thead>
          <tr>
            <th className={headCls}>Role</th>
            <th className={headCls}>Name</th>
          </tr>
        </thead>
        <tbody>
          {list.map((item, i) => {
            if (typeof item !== 'object' || item === null) {
              return (
                <tr key={i}>
                  <td className={cellCls} colSpan={2}>{String(item)}</td>
                </tr>
              );
            }
            const role = item.role || item.position || item.title || null;
            const name = item.name || item.member_name || null;
            const fallback = item.value != null ? String(item.value) : null;
            return (
              <tr key={i}>
                <td className={cellCls}>{role || (!name && fallback) || '—'}</td>
                <td className={cellCls}>{name || (role && fallback) || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function objectListItemTitle(item, fieldDef, index) {
  if (!item || typeof item !== 'object') {
    return `${fieldDef.itemLabel || 'Item'} ${index + 1}`;
  }
  const titleKey = fieldDef.itemTitleKey;
  if (titleKey && item[titleKey]) {
    return `${fieldDef.itemLabel || 'Item'} — ${item[titleKey]}`;
  }
  const listKey = fieldDef.key || '';
  if (listKey === 'adaptive_assessments' || listKey === 'behavioral_assessments') {
    const rtype = String(item.responder_type || '').toLowerCase();
    const tool = item.test_name || item.tool || (listKey === 'adaptive_assessments' ? 'Vineland' : 'BASC');
    if (rtype === 'parent') return `Parent — ${tool}`;
    if (rtype === 'teacher') return `Teacher — ${tool}`;
    if (item.responder) return `${item.responder} — ${tool}`;
    return tool;
  }
  return (
    item.assessment || item.service_type || item.test_name || item.tool || item.name
    || item.goal_area || item.role
    || (item.value != null ? String(item.value) : null)
    || `${fieldDef.itemLabel || 'Item'} ${index + 1}`
  );
}

function ReadOnlyObjectList({ items, fieldDef, compact = false }) {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return <span className="text-gray-400 dark:text-slate-500">—</span>;

  if (compact && fieldDef.key === 'team_members') {
    return <ReadOnlyTeamTable items={list} compact={compact} />;
  }
  if (fieldDef.key === 'team_participation') {
    return <ReadOnlyTeamParticipationTable items={list} compact={compact} />;
  }
  if (fieldDef.key === 'eligibility_form_checkboxes') {
    return (
      <ReadOnlyYesNoPairs
        pairs={list.map((row) => ({
          label: compactFactorLabel(row?.item || row?.label),
          value: row?.value,
        }))}
        compact={compact}
      />
    );
  }

  const itemFields = fieldDef.itemFields || [];
  return (
    <div className="space-y-2 max-h-[36rem] overflow-y-auto">
      {list.map((item, i) => {
        if (typeof item !== 'object' || item === null) {
          return <div key={i} className="text-sm">{String(item)}</div>;
        }
        const name = objectListItemTitle(item, fieldDef, i);
        const entries = itemFields.length
          ? itemFields
              .map((f) => [f, item[f.key]])
              .filter(([f, v]) => {
                const key = typeof f === 'object' ? f.key : f;
                if (fieldDef.itemTitleKey && key === fieldDef.itemTitleKey) return false;
                if (typeof f === 'object' && f.display === 'check_na') return true;
                if (fieldDef.key === 'team_members' && f.key === 'role') return true;
                if (isEmptyValue(v)) return false;
                if (fieldDef.key === 'behavioral_assessments' && (f.key === 'summary' || f.key === 'full_results')) {
                  const hasSubscales = [
                    'hyperactivity', 'aggression', 'conduct_problems', 'attention_problems',
                    'learning_problems', 'behavioral_symptoms_index', 'adaptive_skills_index',
                  ].some((k) => {
                    const sub = item[k];
                    return sub && typeof sub === 'object' && (sub.ss != null || sub.percentile != null);
                  });
                  if (hasSubscales && looksLikeFormJsonBlob(v)) return false;
                  if (hasSubscales && f.key === 'full_results' && String(v).length > 600) return false;
                }
                return true;
              })
          : Object.entries(item).filter(([k, v]) => !String(k).startsWith('_') && !isEmptyValue(v));

        return (
          <div
            key={i}
            className="rounded-lg bg-gray-50 dark:bg-slate-900/50 border border-gray-100 dark:border-slate-700/50 p-3 text-sm"
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="font-semibold text-gray-900 dark:text-white text-xs">{name}</div>
              <RowSourceChip row={item} label={String(name)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
              {entries.map(([f, v]) => {
                const key = typeof f === 'object' ? f.key : f;
                const label = typeof f === 'object' ? (f.label || formatKey(key)) : formatKey(key);
                const field = typeof f === 'object' ? f : { key, type: 'string', label };
                return (
                  <div key={key} className="text-xs min-w-0">
                    <div className="text-gray-400 dark:text-slate-500 font-medium mb-0.5">{label}</div>
                    <ReadOnlyFieldValue field={field} value={v} compact={compact} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ReadOnlyGenericValue({ value }) {
  if (isEmptyValue(value)) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  if (typeof value === 'boolean') return <span className="text-sm">{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'string' && /^(true|false|yes|no)$/i.test(value.trim())) {
    return <span className="text-sm">{/^(true|yes)$/i.test(value.trim()) ? 'Yes' : 'No'}</span>;
  }
  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) {
      return (
        <ul className="list-disc list-inside space-y-0.5 text-sm">
          {value.map((item, i) => <li key={i}>{formatDisplayValue(item)}</li>)}
        </ul>
      );
    }
    return <ReadOnlyObjectList items={value} fieldDef={{ itemLabel: 'Item' }} />;
  }
  if (typeof value === 'object') {
    return <ReadOnlyObjectEditor value={value} fieldDef={{ fields: [] }} />;
  }
  return <span className="text-sm text-gray-700 dark:text-slate-200 break-words">{String(value)}</span>;
}

const SCORE_COL_STANDARD = 'Standard Score';
const PERCENTILE_COL_LABEL = 'Percentile Rank';
const LEVEL_COL_LABEL = 'Descriptive Category';
const STRENGTH_WEAKNESS_COL_LABEL = 'Strength / Weakness';
const VINELAND_ADAPTIVE_LEVELS = new Set([
  'high', 'moderately high', 'adequate', 'moderately low', 'low',
]);
const WISC_CI_RE = /^\s*\d{2,3}\s*[-–]\s*\d{2,3}\s*$/;

function wiscQualitativeFromSs(ss) {
  const n = Number(ss);
  if (!Number.isFinite(n)) return '';
  if (n >= 130) return 'Extremely High';
  if (n >= 120) return 'Very High';
  if (n >= 110) return 'High Average';
  if (n >= 90) return 'Average';
  if (n >= 80) return 'Low Average';
  if (n >= 70) return 'Very Low';
  if (n >= 40) return 'Extremely Low';
  return '';
}

function cognitiveDescriptiveCategory(sub) {
  if (!sub || typeof sub !== 'object') return '';
  const raw = String(sub.description || '').trim();
  if (raw && !WISC_CI_RE.test(raw) && !/\d/.test(raw)) return raw;
  return wiscQualitativeFromSs(sub.ss);
}

const ASSESSMENT_TABLE_KEYS = new Set([
  'academic_standardized', 'classroom_assessments',
  'adaptive_assessments', 'behavioral_assessments',
]);

const ASSESSMENT_META_KEYS = new Set([
  'test_name', 'tool', 'date', 'examiner', 'results', 'full_results',
  'summary', 'responder', 'responder_type', 'adaptive_level', 'over_6_years',
  'subtests', 'scores', 'subdomains', 'relationship',
  // BASC booklet cover / meta — never treat as Standard Score / T rows
  'form', 'relation', 'child_name', 'grade', 'age', 'school',
  'rater_name', 'rater_relationship', 'child_dob', 'child_gender',
  'child', 'name', 'dob',
  '_table_source', '_eval_elig_page_type',
]);

const VINELAND_DOMAIN_ORDER = [
  ['adaptive_behavior_composite', 'ABC - Adaptive Behavior Composite'],
  ['communication', 'Communication'],
  ['daily_living_skills', 'Daily Living Skills'],
  ['socialization', 'Socialization'],
  ['motor_skills', 'Motor Skills'],
];

function vinelandDomainKeyFromLabel(label) {
  const n = String(label || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!n) return null;
  if (n === 'abc' || n.includes('adaptive behavior composite')) return 'adaptive_behavior_composite';
  const hit = VINELAND_DOMAIN_ORDER.find(([, lab]) => lab.toLowerCase() === n);
  return hit ? hit[0] : null;
}

function vinelandDisplayLabel(domainKey, fallback) {
  const hit = VINELAND_DOMAIN_ORDER.find(([k]) => k === domainKey);
  return hit ? hit[1] : (fallback || '—');
}

function isVinelandAssessmentRow(row) {
  const blob = `${row?.test_name || ''} ${row?.tool || ''} ${row?.form || ''}`.toLowerCase();
  return blob.includes('vineland') || /domain[\s-]*level/.test(blob);
}

const VINELAND_FAKE_RESPONDERS = new Set([
  'adaptive behavior composite',
  'adaptive assessment',
  'communication',
  'daily living skills',
  'socialization',
  'motor skills',
  'abc',
  'composite',
]);

/** Coverless SCORE SUMMARY saved as its own tool — never show this card. */
function isVinelandCoverlessJunk(row) {
  if (!row || typeof row !== 'object') return false;
  const name = String(row.test_name || row.tool || '').trim().toLowerCase();
  const resp = String(row.responder || row.rater_name || '').trim().toLowerCase();
  if (name === 'adaptive assessment' || name === 'adaptive') return true;
  if (VINELAND_FAKE_RESPONDERS.has(resp)) return true;
  return false;
}

/** PDF Percentile Rank is a plain number (or <1) — never "1th" / "3rd". */
function formatPercentileRank(value) {
  if (value == null || value === '') return '';
  let s = String(value).trim().replace(/%(?:ile)?$/i, '').trim();
  s = s.replace(/\b(\d+(?:\.\d+)?)(?:st|nd|rd|th)\b/gi, '$1');
  // Keep a space after "<" so "<1" cannot render/read as "+1".
  s = s.replace(/^<\s*/, '< ');
  return s;
}

function vinelandAdaptiveLevel(v) {
  if (!v || typeof v !== 'object') return '';
  const level = String(v.adaptive_level || '').trim();
  if (VINELAND_ADAPTIVE_LEVELS.has(level.toLowerCase())) return level;
  const desc = String(v.description || '').trim();
  if (VINELAND_ADAPTIVE_LEVELS.has(desc.toLowerCase())) return desc;
  return '';
}

function vinelandStrengthWeakness(v) {
  if (!v || typeof v !== 'object') return '';
  const sw = String(v.strength_weakness || '').trim();
  if (/^(strength|weakness)$/i.test(sw)) return sw;
  const desc = String(v.description || '').trim();
  if (/^(strength|weakness)$/i.test(desc)) return desc;
  return '';
}

/** Vineland SCORE SUMMARY order: Composite first, then Domains (PDF format). */
function buildVinelandScoreRows(row) {
  const out = [];
  const seen = new Set();
  const push = (label, domainKey, ss, percentile, description, strengthWeakness, isComposite = false) => {
    const key = domainKey || vinelandDomainKeyFromLabel(label);
    const display = vinelandDisplayLabel(key, label);
    const nameKey = String(display || '').toLowerCase();
    if (!nameKey || seen.has(nameKey)) return;
    if (isEmptyValue(ss) && isEmptyValue(percentile) && isEmptyValue(description) && isEmptyValue(strengthWeakness)) return;
    seen.add(nameKey);
    out.push({
      name: display,
      domainKey: key || null,
      isComposite: !!isComposite || key === 'adaptive_behavior_composite',
      ss: looksLikeNumericScore(ss) ? String(ss).replace(/^T\s*[-]?\s*/i, '') : '',
      percentile: formatPercentileRank(percentile),
      description: description || '',
      strength_weakness: strengthWeakness || '',
    });
  };

  for (const [key, label] of VINELAND_DOMAIN_ORDER) {
    const v = row?.[key];
    if (!v || typeof v !== 'object') continue;
    push(
      label,
      key,
      v.ss ?? v.score,
      v.percentile,
      vinelandAdaptiveLevel(v),
      vinelandStrengthWeakness(v),
      key === 'adaptive_behavior_composite',
    );
  }

  // Schema normalize may fold domains into subtests (and drop domain keys).
  if (Array.isArray(row?.subtests)) {
    for (const s of row.subtests) {
      if (!s || typeof s !== 'object') continue;
      const label = String(s.name || '').trim();
      if (!label) continue;
      const key = vinelandDomainKeyFromLabel(label);
      push(
        label,
        key,
        s.ss ?? s.score,
        s.percentile,
        vinelandAdaptiveLevel(s),
        vinelandStrengthWeakness(s),
        key === 'adaptive_behavior_composite',
      );
    }
  }

  // Fallback: compact full_results line → same table (never leave a text blob).
  if (!out.length) {
    const narrative = String(row?.full_results || row?.results || row?.summary || '');
    const re = /([A-Za-z][A-Za-z /()-]+?):\s*(\d{2,3})(?:\s*\(([^)]*%ile)\))?(?:\s*\[([^\]]+)\])?/g;
    let m;
    while ((m = re.exec(narrative))) {
      const label = m[1].trim();
      const key = vinelandDomainKeyFromLabel(label);
      const pctl = formatPercentileRank((m[3] || '').replace(/\s*%ile/i, '').trim());
      const extra = String(m[4] || '').trim();
      const extraLevel = VINELAND_ADAPTIVE_LEVELS.has(extra.toLowerCase()) ? extra : '';
      const extraSw = /^(strength|weakness)$/i.test(extra) ? extra : '';
      push(label, key, m[2], pctl || '', extraLevel, extraSw, key === 'adaptive_behavior_composite');
    }
    // Ensure Composite-first order when parsed from narrative (often listed last).
    out.sort((a, b) => Number(b.isComposite) - Number(a.isComposite));
  } else {
    out.sort((a, b) => {
      const ai = VINELAND_DOMAIN_ORDER.findIndex(([k]) => k === a.domainKey);
      const bi = VINELAND_DOMAIN_ORDER.findIndex(([k]) => k === b.domainKey);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }
  return out;
}

function isVinelandCompactScoreNarrative(text) {
  const s = String(text || '');
  return /communication\s*:\s*\d{2,3}/i.test(s)
    && /adaptive\s+behavior\s+composite\s*:\s*\d{2,3}/i.test(s);
}

function vinelandSubdomainsForDomain(row, domainKey, domainLabel) {
  const list = Array.isArray(row?.subdomains) ? row.subdomains : [];
  return list.filter((s) => {
    if (!s || typeof s !== 'object') return false;
    if (s.domain_key && domainKey && String(s.domain_key) === domainKey) return true;
    if (domainLabel && String(s.domain || '').toLowerCase() === String(domainLabel).toLowerCase()) return true;
    return false;
  });
}

function vinelandValuePresent(value) {
  return value != null && String(value).trim() !== '';
}

function isVinelandMultiraterRow(row) {
  const src = String(row?._vineland_source || '').toLowerCase();
  if (src === 'multirater') return true;
  return /multirater/i.test(String(row?.form || ''));
}

/** Format 1 = SS + %ile + Strength/Weakness. Format 2 = SS + subdomain v-scale only. */
function vinelandTableColumns(row, subs) {
  const list = Array.isArray(subs) ? subs : [];
  const formatTwo = isVinelandMultiraterRow(row) || (
    !list.some((s) => vinelandPercentileBelongs(s.ss, s.percentile))
    && !list.some((s) => vinelandValuePresent(s.strength_weakness))
  );
  if (formatTwo) {
    return { formatTwo: true, showPct: false, showDesc: false, showSW: false };
  }
  return {
    formatTwo: false,
    showPct: list.some((s) => vinelandPercentileBelongs(s.ss, s.percentile)),
    showDesc: false,
    showSW: list.some((s) => vinelandValuePresent(s.strength_weakness)),
  };
}

/** Drop teacher-SS leaked into parent Percentile Rank (72 next to SS 79). */
function vinelandPercentileBelongs(ss, pctl) {
  if (!vinelandValuePresent(pctl)) return false;
  const raw = String(pctl).trim();
  if (raw.startsWith('<')) return true;
  const p = Number(String(pctl).replace(/[^\d.]/g, ''));
  const ssN = Number(String(ss ?? '').replace(/[^\d.]/g, ''));
  if (!Number.isFinite(p)) return true;
  if (!Number.isFinite(ssN)) return true;
  if (p > 99) return false;
  const z = (ssN - 100) / 15;
  const expected = Math.max(1, Math.min(99, Math.round(100 / (1 + Math.exp(-1.702 * z)))));
  if (Math.abs(expected - p) <= 7) return true;
  if (p >= 40 && p <= 160) return false;
  return false;
}

/** Display labels that are cover metadata, never clinical/academic scales. */
const ASSESSMENT_META_LABELS = new Set([
  'form', 'relation', 'child name', 'child', 'grade', 'age', 'school',
  'rater name', 'rater relationship', 'child dob', 'child gender',
  'responder', 'responder type', 'examiner', 'test name', 'tool', 'date',
]);

function isAssessmentMetaKey(k) {
  const key = String(k || '').toLowerCase();
  return ASSESSMENT_META_KEYS.has(k) || ASSESSMENT_META_KEYS.has(key) || key.includes('subtest') || key.startsWith('_');
}

function isAssessmentMetaLabel(name) {
  const n = String(name || '').toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
  return ASSESSMENT_META_LABELS.has(n);
}

/** True only for values that can be a real SS / T / percentile cell — never "PRS-A" / "Mother". */
function looksLikeNumericScore(v) {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'number' && Number.isFinite(v)) return true;
  const s = String(v).trim();
  if (/^\d+(\.\d+)?%?$/.test(s)) return true;
  if (/^T\s*[-]?\s*\d{2,3}$/i.test(s)) return true;
  return false;
}

/** Parse "Hyperactivity: T49" style tokens; reject out-of-range junk like T4. */
function parseCompactTScoreNarrative(text) {
  const blob = String(text || '').trim();
  if (!blob) return [];
  const out = [];
  const re = /([A-Za-z][A-Za-z0-9 &/'+\-().]{1,60}?)\s*:\s*T\s*[-]?\s*(\d{1,3})\b/gi;
  let m;
  while ((m = re.exec(blob)) !== null) {
    const t = Number(m[2]);
    if (!Number.isFinite(t) || t < 20 || t > 120) continue;
    const name = m[1].trim();
    if (isAssessmentMetaLabel(name)) continue;
    out.push({ name, ss: String(t), percentile: '', description: '' });
  }
  return out;
}

/** WJ/KTEA compact lines: "Letter/Word Identification SS 40; Applied Problems SS 60". */
function parseCompactSSNarrative(text) {
  const blob = String(text || '').trim();
  if (!blob) return [];
  const out = [];
  const re = /([A-Za-z][A-Za-z0-9 &/'+\-().]{1,80}?)\s+(?:score\s+)?ss\s*[:\s]*(\d+(?:\.\d+)?)/gi;
  let m;
  while ((m = re.exec(blob)) !== null) {
    const name = m[1].trim();
    if (!name || isAssessmentMetaLabel(name)) continue;
    out.push({ name, ss: String(m[2]), percentile: '', description: '' });
  }
  return out;
}

/** Render one score value that may be a flat value, a { ss, percentile } object, or a list. */
function formatScoreCell(v) {
  if (v === null || v === undefined || v === '') return null;
  if (Array.isArray(v)) {
    const parts = v.map((item) => formatScoreCell(item)).filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  if (typeof v === 'object') {
    const bits = [];
    const label = v.name || v.test_name || v.tool;
    if (v.ss !== undefined && v.ss !== null && v.ss !== '') bits.push(`SS ${v.ss}`);
    if (v.percentile !== undefined && v.percentile !== null && v.percentile !== '') bits.push(`%ile ${v.percentile}`);
    if (v.score !== undefined && v.score !== null && v.score !== '') bits.push(String(v.score));
    if (v.description) bits.push(String(v.description));
    const narrative = v.results || v.full_results || v.summary;
    if (!bits.length && narrative) bits.push(String(narrative));
    const body = bits.length ? bits.join(' · ') : null;
    if (label && body) return `${label}: ${body}`;
    if (label && !body) return String(label);
    if (body) return body;
    const fallback = formatDisplayValue(v);
    return fallback && fallback !== '[object Object]' ? fallback : null;
  }
  const s = String(v);
  return s === '[object Object]' ? null : s;
}

/** Proper table view for assessment lists (one row per test, scores in a Scores column). */
/** A row is empty if it has no test/date/examiner, no numeric score fields, and only
 *  empty or prompt-echo (template) narrative — such rows are hidden instead of shown. */
function isEmptyAssessmentRow(row) {
  if (!row || typeof row !== 'object') return true;
  if (row.test_name || row.tool || row.date || row.examiner) return false;
  const hasScore = Object.entries(row).some(
    ([k, v]) => !isAssessmentMetaKey(k) && !isEmptyValue(v)
  );
  if (hasScore) return false;
  const narrative = row.full_results || row.results || row.summary || '';
  if (narrative && !looksLikePromptEcho(String(narrative))) return false;
  return true;
}

const ACAD_BATTERY_RE = /ktea|woodcock|wj[\s\-]*i{0,3}v|wiat|ctopp|kaufman/i;
const ACAD_NEST_DROP_RE = /^(diagnostic(\s+\d+)?|diagnostic\s+growth)$|i[\-\s]?ready/i;

function filterAcademicTableRows(items) {
  const list = Array.isArray(items)
    ? items.filter((r) => r && typeof r === 'object' && !isEmptyAssessmentRow(r))
    : [];
  const parents = list.filter((r) => ACAD_BATTERY_RE.test(String(r.test_name || r.tool || '')));
  if (!parents.length) return list;
  const nested = new Set();
  for (const row of parents) {
    for (const sub of row.subtests || []) {
      const n = String(sub?.name || sub?.test_name || '').trim().toLowerCase();
      if (n) nested.add(n);
    }
  }
  return list.filter((row) => {
    const name = String(row.test_name || row.tool || '').trim();
    const lname = name.toLowerCase();
    if (ACAD_BATTERY_RE.test(name)) return true;
    if (nested.has(lname) || ACAD_NEST_DROP_RE.test(name)) return false;
    return false;
  });
}

/** PLAAFP-style: "Iris read 18 correct words per minute with 75% accuracy, placing her in the 1st percentile." */
function parseClassroomFluencyNarrative(text) {
  const blob = String(text || '').trim();
  if (!blob) return [];
  const m = blob.match(
    /read\s+(\d+)\s+correct\s+words\s+per\s+minute\s+with\s+(\d+(?:\.\d+)?)%\s+accuracy(?:[^.]{0,80}?(\d+)(?:st|nd|rd|th)\s+percentile)?/i
  );
  if (!m) return [];
  const out = [
    { name: 'Oral Reading Fluency', ss: m[1], percentile: m[3] || '', description: '' },
    { name: 'Accuracy', ss: m[2], percentile: '', description: '' },
  ];
  return out;
}

function buildAssessmentScoreRows(row, { allowLowScores = false } = {}) {
  const seen = new Set();
  const out = [];
  const push = (entry) => {
    if (!entry || typeof entry !== 'object') return;
    const name = String(entry.name || entry.test_name || '').trim();
    if (!name || isAssessmentMetaLabel(name) || seen.has(name.toLowerCase())) return;
    const ss = entry.ss ?? entry.score;
    // Cover fields like Form=PRS-A must never become score rows (even if mistakenly stored as ss).
    if (!isEmptyValue(ss) && !looksLikeNumericScore(ss) && isEmptyValue(entry.percentile)) return;
    if (isEmptyValue(ss) && isEmptyValue(entry.percentile) && isEmptyValue(entry.description)) return;
    seen.add(name.toLowerCase());
    out.push({
      name,
      ss: looksLikeNumericScore(ss) ? String(ss).replace(/^T\s*[-]?\s*/i, '') : '',
      percentile: entry.percentile != null && entry.percentile !== '' ? String(entry.percentile).replace(/%$/, '') : '',
      description: entry.description || '',
    });
  };
  if (Array.isArray(row.subtests)) {
    row.subtests.forEach((s) => push(s));
  }
  Object.entries(row).forEach(([k, v]) => {
    if (isAssessmentMetaKey(k) || isEmptyValue(v)) return;
    if (typeof v === 'object' && !Array.isArray(v)) {
      push({
        name: formatKey(k),
        ss: v.ss ?? v.score ?? v.t_score,
        percentile: v.percentile,
        description: v.description,
      });
      return;
    }
    if (typeof v === 'object') return;
    // Strict: only promote flat scalars that look like real scores.
    if (!looksLikeNumericScore(v)) return;
    push({ name: formatKey(k), ss: String(v) });
  });
  if (!out.length) {
    const narrative = row.full_results || row.results || row.summary;
    parseCompactTScoreNarrative(narrative).forEach(push);
    if (!out.length) {
      parseCompactSSNarrative(narrative).forEach(push);
    }
    if (!out.length && allowLowScores) {
      parseClassroomFluencyNarrative(narrative).forEach(push);
    }
  }
  return out;
}

function scoreRowsToSubtests(rows) {
  return rows
    .filter((r) => r && (r.name || r.ss || r.score))
    .map((r) => ({
      name: r.name || '',
      ss: r.ss || r.score || '',
      percentile: r.percentile || '',
      description: r.description || '',
      strength_weakness: r.strength_weakness || '',
    }));
}

const COG_INDEX_ROWS = [
  ['verbal_comprehension', 'Verbal Comprehension'],
  ['visual_spatial', 'Visual Spatial'],
  ['fluid_reasoning', 'Fluid Reasoning'],
  ['working_memory', 'Working Memory'],
  ['processing_speed', 'Processing Speed'],
  ['full_scale', 'Full Scale'],
];

function cognitiveIndexBlob(data, key) {
  if (key === 'full_scale') {
    const blob = data?.full_scale && typeof data.full_scale === 'object' && !Array.isArray(data.full_scale)
      ? { ...data.full_scale }
      : {};
    if ((blob.ss == null || blob.ss === '') && data?.fsiq != null && data.fsiq !== '' && typeof data.fsiq !== 'object') {
      blob.ss = data.fsiq;
    }
    return blob;
  }
  return data?.[key] && typeof data[key] === 'object' && !Array.isArray(data[key]) ? data[key] : {};
}

function isWjCognitiveAssessment(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false;
  if (obj.format === 'wj_iv_cog') return true;
  if (Array.isArray(obj.table_of_scores) && obj.table_of_scores.length > 0) {
    const blob = `${obj.test_name || ''} ${obj.tool || ''} ${JSON.stringify(obj.table_of_scores.slice(0, 2))}`.toLowerCase();
    if (/woodcock|wj[\s-]*(iv|v|4|5)|comp-knowledge|gen intellectual/.test(blob)) return true;
  }
  const name = `${obj.test_name || ''} ${obj.tool || ''}`.toLowerCase();
  return /woodcock|wj[\s-]*(iv|v|4|5)/.test(name);
}

function formatWjSsDisplay(row) {
  if (!row || typeof row !== 'object') return '—';
  if (row.ss != null && row.ss !== '' && row.ss_band) return `${row.ss} (${row.ss_band})`;
  if (row.ss != null && row.ss !== '') return String(row.ss);
  if (row.ss_band) return String(row.ss_band);
  return '—';
}

function formatWjPrDisplay(row) {
  if (!row || typeof row !== 'object') return '—';
  if (row.pr != null && row.pr !== '' && row.pr_band) return `${row.pr} (${row.pr_band})`;
  if (row.pr != null && row.pr !== '') return String(row.pr);
  if (row.pr_band) return String(row.pr_band);
  return '—';
}

function WjCognitiveScoreTable({ rows, compact = false }) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];
  if (!list.length) return null;
  const cellCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} align-top text-gray-700 dark:text-slate-200`;
  const headCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[640px]">
        <thead>
          <tr>
            <th className={headCls}>CLUSTER/Test</th>
            <th className={headCls}>AE</th>
            <th className={headCls}>EASY to DIFF</th>
            <th className={headCls}>SS (95% Band)</th>
            <th className={headCls}>SS Classification</th>
            <th className={headCls}>PR (95% Band)</th>
          </tr>
        </thead>
        <tbody>
          {list.map((row, i) => {
            const isCluster = String(row.row_type || '').toLowerCase() === 'cluster'
              || /^[A-Z0-9][A-Z0-9\s\-\/()]+$/.test(String(row.cluster_test || ''))
              || /\((Gc|Gf|Gwm|Gs|Ga|Glr|Gv)\)/i.test(String(row.cluster_test || ''));
            return (
              <tr key={`${row.cluster_test || 'row'}-${i}`}>
                <td className={`${cellCls} ${isCluster ? 'font-semibold' : 'font-medium pl-4'}`}>
                  {row.cluster_test || '—'}
                </td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.ae || '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.easy_to_diff || '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{formatWjSsDisplay(row)}</td>
                <td className={cellCls}>{row.classification || '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{formatWjPrDisplay(row)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function WjVariationTable({ title, rows, compact = false }) {
  const list = Array.isArray(rows) ? rows.filter((r) => r && typeof r === 'object') : [];
  if (!list.length) return null;
  const cellCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} align-top text-gray-700 dark:text-slate-200`;
  const headCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;
  return (
    <div className="mt-2">
      <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-900 border-y border-gray-200 dark:border-slate-600">
        {title}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[560px]">
          <thead>
            <tr>
              <th className={headCls}>Name</th>
              <th className={headCls}>Actual</th>
              <th className={headCls}>Predicted</th>
              <th className={headCls}>Difference</th>
              <th className={headCls}>PR</th>
              <th className={headCls}>SD</th>
              <th className={headCls}>Interpretation</th>
            </tr>
          </thead>
          <tbody>
            {list.map((row, i) => (
              <tr key={`${row.cluster_test || 'v'}-${i}`}>
                <td className={`${cellCls} font-medium`}>{row.cluster_test || '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.actual ?? '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.predicted ?? '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.difference ?? '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.pr ?? '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>{row.sd ?? '—'}</td>
                <td className={cellCls}>{row.interpretation || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AssessmentBatteryEditor({
  items,
  onChange,
  scoreHeader = SCORE_COL_STANDARD,
  measureHeader = 'Measure',
  filterItems = null,
  itemLabel = 'Test',
  showExaminer = true,
  showRespondent = false,
}) {
  const rawList = Array.isArray(items) ? items : [];
  const list = filterItems ? filterItems(rawList) : rawList.filter((r) => r && typeof r === 'object' && !isEmptyAssessmentRow(r));
  const cell = `border border-gray-200 dark:border-slate-600 px-1.5 py-0.5 text-[11px] text-gray-700 dark:text-slate-200`;
  const head = `border border-gray-200 dark:border-slate-600 px-1.5 py-0.5 text-[11px] bg-gray-50 dark:bg-slate-900 font-semibold text-gray-500 dark:text-slate-400 text-left`;

  const patchList = (next) => onChange(next);
  const patchItem = (i, patch) => patchList(list.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeItem = (i) => patchList(list.filter((_, idx) => idx !== i));
  const addItem = () => patchList([...list, { test_name: '', date: '', examiner: '', subtests: [] }]);

  const allowLowScores = scoreHeader === 'Score';
  const subsForRow = (row) => {
    if (isVinelandAssessmentRow(row)) {
      const built = scoreRowsToSubtests(buildVinelandScoreRows(row));
      if (built.length) return built;
    }
    const built = scoreRowsToSubtests(buildAssessmentScoreRows(row, { allowLowScores }));
    if (built.length) return built;
    if (Array.isArray(row.subtests) && row.subtests.length) return row.subtests;
    return [];
  };

  const setSubs = (i, subs) => {
    const next = subs.filter((s) => s && (s.name || s.ss || s.score));
    patchItem(i, { subtests: next.length ? next : [] });
  };

  if (!list.length) {
    return (
      <button type="button" onClick={addItem} className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
        + Add {itemLabel}
      </button>
    );
  }

  return (
    <div className="space-y-2">
      {list.map((row, i) => {
        const subs = subsForRow(row);
        const narrative = row.full_results || row.results || row.summary || '';
        const vineland = isVinelandAssessmentRow(row);
        const vineCols = vineland ? vinelandTableColumns(row, subs) : null;
        const showPct = vineCols ? vineCols.showPct : subs.some((s) => !!s.percentile);
        const showDesc = vineCols ? false : subs.some((s) => vinelandValuePresent(s.description));
        const showSW = vineCols ? vineCols.showSW : false;
        return (
          <article key={i} className="rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden">
            <header className="px-2 py-1.5 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={row.test_name || row.tool || ''}
                onChange={(e) => patchItem(i, { test_name: e.target.value, tool: e.target.value })}
                placeholder="Test name"
                className={`${COMPACT_INPUT_CLS} min-w-[120px] flex-1 max-w-xs`}
              />
              <DatePicker
                value={row.date || ''}
                onChange={(v) => patchItem(i, { date: v || '' })}
                className={`${COMPACT_INPUT_CLS} w-[118px]`}
                placeholder="Date"
                aria-label="Test date"
              />
              {showRespondent ? (
                <input
                  type="text"
                  value={row.responder || row.rater_name || ''}
                  onChange={(e) => patchItem(i, { responder: e.target.value, rater_name: e.target.value })}
                  placeholder="Respondent"
                  className={`${COMPACT_INPUT_CLS} min-w-[100px] flex-1 max-w-[160px]`}
                />
              ) : null}
              {showExaminer ? (
                <input
                  type="text"
                  value={row.examiner || ''}
                  onChange={(e) => patchItem(i, { examiner: e.target.value })}
                  placeholder="Examiner"
                  className={`${COMPACT_INPUT_CLS} min-w-[100px] flex-1 max-w-[160px]`}
                />
              ) : null}
              <button type="button" onClick={() => removeItem(i)} className="text-[10px] text-red-500 ml-auto">Remove</button>
            </header>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={head}>{measureHeader}</th>
                    <th className={head}>{scoreHeader}</th>
                    {showPct ? <th className={head}>{PERCENTILE_COL_LABEL}</th> : null}
                    {showDesc ? <th className={head}>{LEVEL_COL_LABEL}</th> : null}
                    {showSW ? <th className={head}>{STRENGTH_WEAKNESS_COL_LABEL}</th> : null}
                    <th className={`${head} w-6`} />
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s, j) => (
                    <tr key={j}>
                      <td className={cell}>
                        <input
                          type="text"
                          value={s.name || ''}
                          onChange={(e) => {
                            const next = [...subs];
                            next[j] = { ...next[j], name: e.target.value };
                            setSubs(i, next);
                          }}
                          className={COMPACT_INPUT_CLS}
                        />
                      </td>
                      <td className={cell}>
                        <input
                          type="text"
                          value={s.ss ?? s.score ?? ''}
                          onChange={(e) => {
                            const next = [...subs];
                            next[j] = { ...next[j], ss: e.target.value };
                            setSubs(i, next);
                          }}
                          className={`${COMPACT_INPUT_CLS} w-14`}
                        />
                      </td>
                      {showPct ? (
                        <td className={cell}>
                          <input
                            type="text"
                            value={s.percentile || ''}
                            onChange={(e) => {
                              const next = [...subs];
                              next[j] = { ...next[j], percentile: e.target.value };
                              setSubs(i, next);
                            }}
                            className={`${COMPACT_INPUT_CLS} w-12`}
                          />
                        </td>
                      ) : null}
                      {showDesc ? (
                        <td className={cell}>
                          <input
                            type="text"
                            value={s.description || ''}
                            onChange={(e) => {
                              const next = [...subs];
                              next[j] = { ...next[j], description: e.target.value };
                              setSubs(i, next);
                            }}
                            className={COMPACT_INPUT_CLS}
                          />
                        </td>
                      ) : null}
                      {showSW ? (
                        <td className={cell}>
                          <input
                            type="text"
                            value={s.strength_weakness || ''}
                            onChange={(e) => {
                              const next = [...subs];
                              next[j] = { ...next[j], strength_weakness: e.target.value };
                              setSubs(i, next);
                            }}
                            className={COMPACT_INPUT_CLS}
                          />
                        </td>
                      ) : null}
                      <td className={cell}>
                        <button
                          type="button"
                          onClick={() => setSubs(i, subs.filter((_, idx) => idx !== j))}
                          className="text-red-400 hover:text-red-600"
                          title="Remove row"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-2 py-1 flex flex-wrap gap-2 border-t border-gray-100 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setSubs(i, [...subs, { name: '', ss: '' }])}
                className="text-[10px] text-indigo-600 dark:text-indigo-400"
              >
                + Score row
              </button>
              {!subs.length && narrative ? (
                <span className="text-[10px] text-gray-400 truncate max-w-full" title={String(narrative)}>
                  Narrative only — add score rows above
                </span>
              ) : null}
            </div>
          </article>
        );
      })}
      <button type="button" onClick={addItem} className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
        + Add {itemLabel}
      </button>
    </div>
  );
}

function CognitiveAssessmentEditor({ value, onChange }) {
  const obj = unwrapSingleObject(value);
  const data = obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {};
  const patch = (next) => onChange({ ...data, ...next });
  const patchIndex = (key, sub) => {
    const cur = cognitiveIndexBlob(data, key);
    const next = { [key]: { ...cur, ...sub } };
    if (key === 'full_scale' && sub.ss !== undefined) {
      next.fsiq = sub.ss;
    }
    patch(next);
  };
  const cell = `border border-gray-200 dark:border-slate-600 px-1.5 py-0.5 text-[11px]`;
  const head = `border border-gray-200 dark:border-slate-600 px-1.5 py-0.5 text-[11px] bg-gray-50 dark:bg-slate-900 font-semibold text-gray-500 dark:text-slate-400 text-left`;

  if (isWjCognitiveAssessment(data)) {
    const tableRows = Array.isArray(data.table_of_scores) ? data.table_of_scores : [];
    return (
      <article className="rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600">
          <input
            type="text"
            value={data.test_name || ''}
            onChange={(e) => patch({ test_name: e.target.value, format: 'wj_iv_cog' })}
            placeholder="Woodcock-Johnson IV Tests of Cognitive"
            className={COMPACT_INPUT_CLS}
          />
          <DatePicker value={data.date || ''} onChange={(v) => patch({ date: v || '', format: 'wj_iv_cog' })} className={COMPACT_INPUT_CLS} placeholder="Date" aria-label="WJ test date" />
          <input type="text" value={data.examiner || ''} onChange={(e) => patch({ examiner: e.target.value, format: 'wj_iv_cog' })} placeholder="Examiner" className={COMPACT_INPUT_CLS} />
          <input type="text" value={data.norms || ''} onChange={(e) => patch({ norms: e.target.value, format: 'wj_iv_cog' })} placeholder="Norms (e.g. age 8-11)" className={COMPACT_INPUT_CLS} />
        </div>
        <WjCognitiveScoreTable rows={tableRows} />
        <WjVariationTable title="VARIATIONS" rows={data.variations} />
        <WjVariationTable title="COMPARISONS" rows={data.comparisons} />
      </article>
    );
  }

  const indexRows = COG_INDEX_ROWS.map(([key, label]) => ({
    key,
    label,
    sub: cognitiveIndexBlob(data, key),
  }));

  return (
    <article className="rounded-md border border-gray-200 dark:border-slate-600 overflow-hidden">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 p-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600">
        <input
          type="text"
          value={data.test_name || ''}
          onChange={(e) => patch({ test_name: e.target.value })}
          placeholder="Test"
          className={COMPACT_INPUT_CLS}
        />
        <DatePicker value={data.date || ''} onChange={(v) => patch({ date: v || '' })} className={COMPACT_INPUT_CLS} placeholder="Date" aria-label="Cognitive test date" />
        <input type="text" value={data.examiner || ''} onChange={(e) => patch({ examiner: e.target.value })} placeholder="Examiner" className={COMPACT_INPUT_CLS} />
        <div className="flex gap-1">
          <input
            type="text"
            value={typeof data.fsiq === 'object' ? (data.fsiq?.ss ?? '') : (data.fsiq ?? '')}
            onChange={(e) => {
              const fsiq = e.target.value;
              const fs = cognitiveIndexBlob(data, 'full_scale');
              patch({ fsiq, full_scale: { ...fs, ss: fsiq } });
            }}
            placeholder="FSIQ"
            className={`${COMPACT_INPUT_CLS} w-1/2`}
          />
          <input type="text" value={data.gai ?? ''} onChange={(e) => patch({ gai: e.target.value })} placeholder="GAI" className={`${COMPACT_INPUT_CLS} w-1/2`} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={head}>Index</th>
              <th className={head}>{SCORE_COL_STANDARD}</th>
              <th className={head}>{PERCENTILE_COL_LABEL}</th>
              <th className={head}>{LEVEL_COL_LABEL}</th>
            </tr>
          </thead>
          <tbody>
            {indexRows.map(({ key, label, sub }) => (
              <tr key={key}>
                <td className={`${cell} font-medium text-gray-700 dark:text-slate-200`}>{label}</td>
                <td className={cell}>
                  <input type="text" value={sub.ss ?? ''} onChange={(e) => patchIndex(key, { ss: e.target.value })} className={`${COMPACT_INPUT_CLS} w-14`} />
                </td>
                <td className={cell}>
                  <input type="text" value={sub.percentile ?? ''} onChange={(e) => patchIndex(key, { percentile: e.target.value })} className={`${COMPACT_INPUT_CLS} w-12`} />
                </td>
                <td className={cell}>
                  <input type="text" value={cognitiveDescriptiveCategory(sub)} onChange={(e) => patchIndex(key, { description: e.target.value })} className={COMPACT_INPUT_CLS} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

const COMPACT_LIST_META_KEYS = new Set(['test_name', 'tool', 'date', 'examiner', 'responder', 'responder_type', 'form']);

function visibleItemFields(item, itemFields) {
  return (itemFields || []).filter((f) => {
    if (COMPACT_LIST_META_KEYS.has(f.key) || f.type === 'boolean') return true;
    return !isEmptyValue(item?.[f.key]);
  });
}

function CompactObjectListEditor({ items, onChange, fieldDef }) {
  const list = Array.isArray(items) ? items : [];
  const itemFields = fieldDef.itemFields || [];
  const add = () => onChange([...list, emptyItemFromFields(itemFields)]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateItem = (i, key, val) => onChange(list.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  return (
    <div className="space-y-2">
      {list.map((item, i) => {
        const fields = visibleItemFields(item, itemFields);
        return (
          <div key={i} className="rounded-md border border-gray-200 dark:border-slate-700 p-2">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[11px] font-semibold text-gray-600 dark:text-slate-300">
                {objectListItemTitle(item, fieldDef, i)}
              </span>
              <button type="button" onClick={() => remove(i)} className="text-[10px] text-red-500">Remove</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-1">
              {fields.map((sub) => (
                <div key={sub.key} className="flex items-center gap-1.5 min-w-0">
                  <label className="text-[10px] text-gray-500 w-20 shrink-0 truncate" title={sub.label}>{sub.label}</label>
                  <div className="flex-1 min-w-0">
                    <FieldInput fieldDef={sub} value={item?.[sub.key]} onChange={(val) => updateItem(i, sub.key, val)} compact />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <button type="button" onClick={add} className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
        + Add {fieldDef.itemLabel || 'item'}
      </button>
    </div>
  );
}

function AssessmentBatteryList({
  items,
  compact = false,
  scoreHeader = SCORE_COL_STANDARD,
  measureHeader = 'Measure',
  hideNarrativeIfSubtests = false,
  // Hide Percentile Rank / Descriptive Category when this card has no value there.
  // Vineland uses this so a two-column report does not show an empty column.
  // BASC keeps the full format.
  hideEmptyOptionalColumns = false,
  showExaminer = true,
  // Vineland cover uses Respondent Name (not Examiner Name).
  showRespondent = false,
  // Classroom: Measure left, Score right — matches Acadience card layout.
  alignScoreEnd = false,
  // Classroom only: never add %ile / category columns (keeps Benchmark aligned with Math/DIBELS).
  forceTwoColumnScores = false,
}) {
  const list = Array.isArray(items)
    ? items.filter((r) => r && typeof r === 'object' && !isEmptyAssessmentRow(r))
    : [];
  const [expandedVineland, setExpandedVineland] = useState(() => ({}));
  if (!list.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  // Classroom uses "Score" (raw/composite); do not apply BASC T-score floor.
  const allowLowScores = scoreHeader === 'Score';

  const cellCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} align-top text-gray-700 dark:text-slate-200`;
  const headCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;
  const scoreHeadCls = alignScoreEnd ? `${headCls} text-right w-[5.5rem]` : headCls;
  const scoreCellCls = alignScoreEnd
    ? `${cellCls} whitespace-nowrap text-right w-[5.5rem]`
    : `${cellCls} whitespace-nowrap`;
  const optHeadCls = alignScoreEnd ? `${headCls} text-right w-[7rem]` : headCls;
  const optCellCls = alignScoreEnd
    ? `${cellCls} whitespace-nowrap text-right w-[7rem]`
    : `${cellCls} whitespace-nowrap`;

  const toggleVinelandDomain = (rowKey, domainKey) => {
    const key = `${rowKey}::${domainKey}`;
    setExpandedVineland((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {list.map((row, i) => {
        const vineland = isVinelandAssessmentRow(row);
        const subs = vineland
          ? buildVinelandScoreRows(row)
          : buildAssessmentScoreRows(row, { allowLowScores });
        const nestedByName = nestedRowsByName(row);
        const subSource = (name) => nestedByName.get(String(name || '').trim().toLowerCase()) || null;
        const vineCols = vineland ? vinelandTableColumns(row, subs) : null;
        const showPct = !forceTwoColumnScores && (vineCols
          ? vineCols.showPct
          : (!hideEmptyOptionalColumns || subs.some((s) => !!s.percentile)));
        const showDesc = !forceTwoColumnScores && !vineland && (
          !hideEmptyOptionalColumns || subs.some((s) => s.description)
        );
        const showSW = !forceTwoColumnScores && !!vineCols?.showSW;
        const results = row.full_results || row.results || row.summary || '';
        const invalidTLine = (() => {
          const blob = String(results || '').trim();
          if (!blob || blob.length > 400) return false;
          const vals = [];
          const re = /:\s*T\s*[-]?\s*(\d{1,3})\b/gi;
          let m;
          while ((m = re.exec(blob))) vals.push(Number(m[1]));
          return vals.length >= 2 && vals.every((n) => Number.isFinite(n) && n < 20);
        })();
        const showNarrative = (() => {
          if (!results || invalidTLine) return false;
          // Vineland: never show the semicolon score blob when a table can render.
          if (vineland && (subs.length || isVinelandCompactScoreNarrative(results))) return false;
          if (hideNarrativeIfSubtests && subs.length) return false;
          return true;
        })();
        return (
          <article
            key={i}
            className="rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-800"
          >
            <header className={`px-3 ${compact ? 'py-1.5' : 'py-2'} bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 flex flex-wrap items-baseline gap-x-4 gap-y-0.5`}>
              <h4 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900 dark:text-white`}>
                {row.test_name || row.tool || 'Test'}
              </h4>
              <span className="text-[11px] text-gray-500 dark:text-slate-400">
                Date: {row.date ? (
                  row.over_6_years ? (
                    <span className="text-amber-600 dark:text-amber-400" title="Over 6 years old at the evaluation date (visual only)">
                      {row.date}
                    </span>
                  ) : row.date
                ) : '—'}
              </span>
              {showRespondent ? (
                <span className="text-[11px] text-gray-500 dark:text-slate-400">
                  Respondent: {row.responder || row.rater_name || 'N/A'}
                </span>
              ) : null}
              {showExaminer ? (
                <span className="text-[11px] text-gray-500 dark:text-slate-400">
                  Examiner: {row.examiner || 'N/A'}
                </span>
              ) : null}
              {row.form ? (
                <span className="text-[11px] text-gray-500 dark:text-slate-400">Form: {row.form}</span>
              ) : null}
              {row.relation || row.relationship || row.rater_relationship ? (
                <span className="text-[11px] text-gray-500 dark:text-slate-400">
                  Relation: {row.relation || row.relationship || row.rater_relationship}
                </span>
              ) : null}
              {row.responder_type ? (
                <span className="text-[11px] text-gray-500 dark:text-slate-400">
                  Rater: {row.responder_type}
                </span>
              ) : null}
              <span className="ml-auto">
                <RowSourceChip row={row} label={row.test_name || row.tool || 'Test'} />
              </span>
            </header>
            {subs.length > 0 && (
              <div className="overflow-x-auto">
                <table className={`w-full border-collapse${alignScoreEnd ? ' table-fixed' : ''}`}>
                  <thead>
                    <tr>
                      <th className={headCls}>{measureHeader}</th>
                      <th className={scoreHeadCls}>{vineland ? scoreHeader : scoreHeader}</th>
                      {showPct ? <th className={optHeadCls}>{PERCENTILE_COL_LABEL}</th> : null}
                      {showDesc ? <th className={optHeadCls}>{LEVEL_COL_LABEL}</th> : null}
                      {showSW ? <th className={optHeadCls}>{STRENGTH_WEAKNESS_COL_LABEL}</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {vineland ? (
                      <>
                        {subs.map((s, j) => {
                          const domainSubs = !s.isComposite
                            ? vinelandSubdomainsForDomain(row, s.domainKey, s.name)
                            : [];
                          const showSubRaw = !vineCols?.formatTwo && domainSubs.some((sd) => vinelandValuePresent(sd.raw));
                          const showSubSW = !vineCols?.formatTwo && domainSubs.some((sd) => {
                            const sw = vinelandStrengthWeakness(sd);
                            return vinelandValuePresent(sw);
                          });
                          const expandKey = `${i}::${s.domainKey || s.name}`;
                          const open = !!expandedVineland[expandKey];
                          const showDomainsHeader = !s.isComposite
                            && (j === 0 || subs[j - 1]?.isComposite);
                          const displayName = s.name || '—';
                          return (
                            <React.Fragment key={j}>
                              {showDomainsHeader ? (
                                <tr>
                                  <td
                                    colSpan={(showPct ? 1 : 0) + (showDesc ? 1 : 0) + (showSW ? 1 : 0) + 2}
                                    className={`${cellCls} bg-gray-50/80 dark:bg-slate-900/60 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400`}
                                  >
                                    Domains
                                  </td>
                                </tr>
                              ) : null}
                              <tr className={s.isComposite ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : undefined}>
                                <td className={`${cellCls} font-medium`}>
                                  <span className="inline-flex items-center gap-1.5">
                                    {domainSubs.length > 0 ? (
                                      <button
                                        type="button"
                                        onClick={() => toggleVinelandDomain(i, s.domainKey || s.name)}
                                        className="inline-flex h-5 w-5 items-center justify-center rounded border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                                        aria-expanded={open}
                                        title={open ? 'Hide subdomain scores' : 'Show subdomain scores'}
                                      >
                                        <span className="text-[10px] leading-none">{open ? '▾' : '▸'}</span>
                                      </button>
                                    ) : null}
                                    <span>{displayName}</span>
                                  </span>
                                </td>
                                <td className={scoreCellCls}>{s.ss || s.score || '—'}</td>
                                {showPct ? (
                                  <td className={optCellCls}>{s.percentile ? `${s.percentile}` : '—'}</td>
                                ) : null}
                                {showDesc ? (
                                  <td className={optCellCls}>{s.description || '—'}</td>
                                ) : null}
                                {showSW ? (
                                  <td className={optCellCls}>{s.strength_weakness || '—'}</td>
                                ) : null}
                              </tr>
                              {open && domainSubs.length > 0 ? (
                                <tr>
                                  <td
                                    colSpan={(showPct ? 1 : 0) + (showDesc ? 1 : 0) + (showSW ? 1 : 0) + 2}
                                    className="border border-gray-200 dark:border-slate-600 px-2 py-2 bg-slate-50/80 dark:bg-slate-900/40"
                                  >
                                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-1.5">
                                      Subdomain Score Summary — {s.name}
                                    </p>
                                    <table className="w-full border-collapse">
                                      <thead>
                                        <tr>
                                          <th className={headCls}>Subdomain</th>
                                          <th className={scoreHeadCls}>v-Scale</th>
                                          {showSubRaw ? <th className={optHeadCls}>Raw</th> : null}
                                          {showSubSW ? <th className={optHeadCls}>{STRENGTH_WEAKNESS_COL_LABEL}</th> : null}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {domainSubs.map((sd, si) => (
                                          <tr key={si}>
                                            <td className={`${cellCls} font-medium pl-4`}>
                                              <span className="flex items-center justify-between gap-2">
                                                <span>{sd.name || '—'}</span>
                                                <RowSourceChip row={sd} label={`${row.test_name || 'Vineland'} — ${sd.name}`} />
                                              </span>
                                            </td>
                                            <td className={scoreCellCls}>
                                              {vinelandValuePresent(sd.v_scale) ? String(sd.v_scale) : '—'}
                                            </td>
                                            {showSubRaw ? (
                                              <td className={optCellCls}>
                                                {vinelandValuePresent(sd.raw) ? String(sd.raw) : '—'}
                                              </td>
                                            ) : null}
                                            {showSubSW ? (
                                              <td className={optCellCls}>
                                                {vinelandStrengthWeakness(sd) || '—'}
                                              </td>
                                            ) : null}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              ) : null}
                            </React.Fragment>
                          );
                        })}
                      </>
                    ) : (
                      subs.map((s, j) => (
                        <tr key={j}>
                          <td className={`${cellCls} font-medium`}>
                            <span className="flex items-center justify-between gap-2">
                              <span>{s.name || '—'}</span>
                              <RowSourceChip row={subSource(s.name)} label={`${row.test_name || row.tool || 'Test'} — ${s.name}`} />
                            </span>
                          </td>
                          <td className={scoreCellCls}>{s.ss || s.score || '—'}</td>
                          {showPct ? (
                            <td className={optCellCls}>{s.percentile ? `${s.percentile}` : '—'}</td>
                          ) : null}
                          {showDesc ? (
                            <td className={alignScoreEnd ? `${cellCls} text-right` : cellCls}>
                              {s.description || '—'}
                            </td>
                          ) : null}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {showNarrative ? (
              <p className={`px-3 ${compact ? 'py-1.5 text-[11px]' : 'py-2 text-xs'} text-gray-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap break-words border-t border-gray-100 dark:border-slate-700`}>
                {sanitizeAssessmentNarrative(String(results))}
              </p>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function SurveyBatteryList({ items, compact = false, scoreHeader = 'T', measureHeader = 'Scale' }) {
  return (
    <AssessmentBatteryList
      items={items}
      compact={compact}
      scoreHeader={scoreHeader}
      measureHeader={measureHeader}
      hideNarrativeIfSubtests
    />
  );
}

function BehavioralBatteryList({ items, compact = false }) {
  return <SurveyBatteryList items={items} compact={compact} scoreHeader="T" measureHeader="Scale" />;
}

function AdaptiveBatteryList({ items, compact = false }) {
  const list = (Array.isArray(items) ? items : []).filter((row) => !isVinelandCoverlessJunk(row));
  return (
    <AssessmentBatteryList
      items={list}
      compact={compact}
      scoreHeader={SCORE_COL_STANDARD}
      measureHeader="Domain"
      hideNarrativeIfSubtests
      hideEmptyOptionalColumns
      showRespondent
      showExaminer
    />
  );
}

function AcademicBatteryList({ items, compact = false }) {
  return (
    <AssessmentBatteryList
      items={filterAcademicTableRows(items)}
      compact={compact}
      scoreHeader={SCORE_COL_STANDARD}
      measureHeader="Subtest"
      hideNarrativeIfSubtests
      hideEmptyOptionalColumns
      showExaminer={false}
    />
  );
}

function ClassroomBatteryList({ items, compact = false }) {
  return (
    <AssessmentBatteryList
      items={items}
      compact={compact}
      scoreHeader="Score"
      hideNarrativeIfSubtests
      // Always Measure + Score only so every card (incl. Reading Benchmark) shares one alignment.
      hideEmptyOptionalColumns
      forceTwoColumnScores
      showExaminer={false}
      alignScoreEnd
    />
  );
}

/* ---------- Standardized Assessments (verified): assessment-engine rows ---------- */

const ENGINE_COMPOSITE_TABLES = new Set(['composites', 'indexes', 'clusters', 'domains', 'quotients']);
const ENGINE_PART_TABLES = new Set(['subtests', 'tests', 'scales', 'subdomains', 'subscales']);

// Composite -> the subtests shown under it. A subtest that counts toward several composites
// sits under the first listed composite the report actually has; broad totals come last so
// they only collect subtests whose own composite was not read.
const ENGINE_COMPOSITE_PARTS = [
  {
    re: /ktea/i,
    groups: [
      ['Reading', ['Letter & Word Recognition', 'Reading Comprehension']],
      ['Math', ['Math Concepts & Applications', 'Math Computation']],
      ['Written Language', ['Written Expression', 'Spelling']],
      ['Sound-Symbol', ['Phonological Processing', 'Nonsense Word Decoding']],
      ['Decoding', ['Letter & Word Recognition', 'Nonsense Word Decoding']],
      ['Reading Fluency', ['Silent Reading Fluency', 'Word Recognition Fluency', 'Decoding Fluency']],
      ['Reading Understanding', ['Reading Comprehension', 'Reading Vocabulary']],
      ['Oral Language', ['Associational Fluency', 'Listening Comprehension', 'Oral Expression']],
      ['Oral Fluency', ['Associational Fluency', 'Object Naming Facility']],
      ['Orthographic Processing', ['Spelling', 'Letter Naming Facility', 'Word Recognition Fluency']],
      ['Academic Fluency', ['Writing Fluency', 'Math Fluency', 'Decoding Fluency']],
      ['Academic Skills Battery', [
        'Letter & Word Recognition', 'Reading Comprehension', 'Math Concepts & Applications',
        'Math Computation', 'Written Expression', 'Spelling',
      ]],
    ],
  },
  {
    re: /wiat/i,
    groups: [
      ['Reading', ['Word Reading', 'Reading Comprehension']],
      ['Written Expression', ['Spelling', 'Sentence Composition', 'Essay Composition', 'Alphabet Writing Fluency']],
      ['Mathematics', ['Math Problem Solving', 'Numerical Operations']],
      ['Math Fluency', ['Math Fluency-Addition', 'Math Fluency-Subtraction', 'Math Fluency-Multiplication']],
      ['Oral Language', ['Listening Comprehension', 'Oral Expression']],
      ['Basic Reading', ['Word Reading', 'Pseudoword Decoding']],
      ['Decoding', ['Pseudoword Decoding', 'Decoding Fluency']],
      ['Reading Fluency', ['Oral Reading Fluency', 'Orthographic Fluency', 'Decoding Fluency']],
      ['Phonological Processing', ['Phonemic Proficiency', 'Pseudoword Decoding']],
      ['Orthographic Processing', ['Orthographic Fluency', 'Orthographic Choice']],
    ],
  },
  {
    re: /wisc/i,
    groups: [
      ['Verbal Comprehension Index', ['Similarities', 'Vocabulary', 'Information', 'Comprehension']],
      ['Visual Spatial Index', ['Block Design', 'Visual Puzzles']],
      ['Fluid Reasoning Index', ['Matrix Reasoning', 'Figure Weights', 'Picture Concepts', 'Arithmetic']],
      ['Working Memory Index', ['Digit Span', 'Picture Span', 'Letter-Number Sequencing']],
      ['Processing Speed Index', ['Coding', 'Symbol Search', 'Cancellation']],
      ['Naming Speed Index', ['Naming Speed Literacy', 'Naming Speed Quantity']],
      ['Symbol Translation Index', [
        'Immediate Symbol Translation', 'Delayed Symbol Translation', 'Recognition Symbol Translation',
      ]],
      ['Full Scale IQ', [
        'Similarities', 'Vocabulary', 'Block Design', 'Matrix Reasoning', 'Figure Weights', 'Digit Span', 'Coding',
      ]],
    ],
  },
  {
    re: /wj.*cog|cognitive abilities/i,
    groups: [
      ['Comprehension-Knowledge (Gc)', ['Oral Vocabulary', 'General Information']],
      ['Fluid Reasoning (Gf)', ['Number Series', 'Concept Formation', 'Analysis-Synthesis']],
      ['Short-Term Working Memory (Gwm)', ['Verbal Attention', 'Numbers Reversed', 'Object-Number Sequencing']],
      ['Cognitive Processing Speed (Gs)', ['Letter-Pattern Matching', 'Pair Cancellation']],
      ['Auditory Processing (Ga)', ['Phonological Processing', 'Nonword Repetition']],
      ['Long-Term Retrieval (Glr)', ['Story Recall', 'Visual-Auditory Learning']],
      ['Visual Processing (Gv)', ['Visualization', 'Picture Recognition']],
      ['Perceptual Speed', ['Letter-Pattern Matching', 'Number-Pattern Matching']],
    ],
  },
  {
    re: /wj|woodcock/i,
    groups: [
      ['Reading', ['Letter-Word Identification', 'Passage Comprehension']],
      ['Mathematics', ['Applied Problems', 'Calculation']],
      ['Written Language', ['Spelling', 'Writing Samples']],
      ['Basic Reading Skills', ['Letter-Word Identification', 'Word Attack']],
      ['Reading Fluency', ['Oral Reading', 'Sentence Reading Fluency']],
      ['Reading Comprehension', ['Passage Comprehension', 'Reading Recall']],
      ['Math Calculation Skills', ['Calculation', 'Math Facts Fluency']],
      ['Math Problem Solving', ['Applied Problems', 'Number Matrices']],
      ['Basic Writing Skills', ['Spelling', 'Editing']],
      ['Written Expression', ['Writing Samples', 'Sentence Writing Fluency']],
      ['Broad Reading', ['Letter-Word Identification', 'Passage Comprehension', 'Sentence Reading Fluency']],
      ['Broad Mathematics', ['Applied Problems', 'Calculation', 'Math Facts Fluency']],
      ['Broad Written Language', ['Spelling', 'Writing Samples', 'Sentence Writing Fluency']],
    ],
  },
  {
    re: /rias/i,
    groups: [
      ['Verbal Intelligence Index (VIX)', ['Guess What (GWH)', 'Verbal Reasoning (VRZ)']],
      ['Nonverbal Intelligence Index (NIX)', ['Odd-Item Out (OIO)', "What's Missing (WHM)"]],
      ['Composite Memory Index (CMX)', ['Verbal Memory (VRM)', 'Nonverbal Memory (NVM)']],
      ['Speeded Processing Index (SPI)', ['Speeded Naming Task (SNT)', 'Speeded Picture Search (SPS)']],
    ],
  },
  {
    re: /bot[\s-]*2|bruininks/i,
    groups: [
      ['Fine Manual Control', ['Fine Motor Precision', 'Fine Motor Integration']],
      ['Manual Coordination', ['Manual Dexterity', 'Upper-Limb Coordination']],
      ['Body Coordination', ['Bilateral Coordination', 'Balance']],
      ['Strength and Agility', ['Running Speed and Agility', 'Strength']],
    ],
  },
  {
    re: /pdms|peabody developmental/i,
    groups: [
      ['Gross Motor Quotient (GMQ)', ['Reflexes', 'Stationary', 'Locomotion', 'Object Manipulation']],
      ['Fine Motor Quotient (FMQ)', ['Grasping', 'Visual-Motor Integration']],
    ],
  },
  {
    re: /vineland/i,
    groups: [
      ['Communication', ['Receptive', 'Expressive', 'Written']],
      ['Daily Living Skills', ['Personal', 'Domestic', 'Community']],
      ['Socialization', ['Interpersonal Relationships', 'Play and Leisure', 'Coping Skills']],
      ['Motor Skills', ['Fine Motor', 'Gross Motor']],
    ],
  },
  {
    re: /basc/i,
    groups: [
      ['Externalizing Problems', ['Hyperactivity', 'Aggression', 'Conduct Problems']],
      ['Internalizing Problems', ['Anxiety', 'Depression', 'Somatization']],
      ['School Problems', ['Attention Problems', 'Learning Problems']],
      ['Behavioral Symptoms Index', ['Hyperactivity', 'Aggression', 'Depression', 'Attention Problems', 'Atypicality', 'Withdrawal']],
      ['Adaptive Skills', [
        'Adaptability', 'Social Skills', 'Leadership', 'Study Skills', 'Functional Communication', 'Activities of Daily Living',
      ]],
    ],
  },
];

// [value key, column header]. Primary columns show whenever a row has the value; the rest
// sit behind "All columns".
const ENGINE_PRIMARY_COLS = [
  ['standard_score', SCORE_COL_STANDARD],
  ['composite_score', 'Composite Score'],
  ['index_score', 'Index Score'],
  ['quotient', 'Quotient'],
  ['t_score', 'T Score'],
  ['scaled_score', 'Scaled Score'],
  ['scale_score', 'Scale Score'],
  ['v_scale', 'v-Scale'],
  ['autism_index', 'Autism Index'],
  ['percentile', PERCENTILE_COL_LABEL],
  ['confidence_interval', 'Confidence Interval'],
  ['confidence_band', 'Confidence Band'],
  ['descriptive_category', LEVEL_COL_LABEL],
  ['qualitative_description', LEVEL_COL_LABEL],
  ['classification', 'Classification'],
  ['descriptive_range', 'Descriptive Range'],
  ['adaptive_level', 'Adaptive Level'],
  ['strength_weakness', STRENGTH_WEAKNESS_COL_LABEL],
  ['severity_level', 'Severity Level'],
  ['probability_of_asd', 'Probability of ASD'],
  ['grade_equivalent', 'Grade Equiv.'],
  ['age_equivalent', 'Age Equiv.'],
  ['test_age_equivalent', 'Test Age Equiv.'],
];
const ENGINE_SECONDARY_COLS = [
  ['raw_score', 'Raw Score'],
  ['total_raw_score', 'Total Raw Score'],
  ['total_point_score', 'Total Point Score'],
  ['GSV', 'GSV'],
  ['growth_scale_value', 'GSV'],
  ['w_score', 'W Score'],
  ['rpi', 'RPI'],
  ['sum_of_subtest_scores', 'Sum of Subtest Scores'],
  ['sum_of_scaled_scores', 'Sum of Scaled Scores'],
  ['sum_of_standard_scores', 'Sum of Standard Scores'],
  ['sum_of_scale_scores', 'Sum of Scale Scores'],
  ['sum_of_t_scores', 'Sum of T Scores'],
  ['Base Rate', 'Base Rate'],
];
const ENGINE_KNOWN_COLS = new Set([...ENGINE_PRIMARY_COLS, ...ENGINE_SECONDARY_COLS].map(([k]) => k));

const ENGINE_FAMILY_SECTIONS = [
  { re: /vineland/i, key: 'adaptive_assessments' },
  { re: /\bbasc/i, key: 'behavioral_assessments' },
];

function engineValuePresent(v) {
  return v !== null && v !== undefined && String(v).trim() !== '';
}

function engineDayNumber(value) {
  const s = String(value || '').trim();
  let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return Date.UTC(+m[3], +m[1] - 1, +m[2]) / 86400000;
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000;
  return null;
}

function engineRaterKind(text) {
  const t = String(text || '').toLowerCase();
  if (/\bparent|\bprs\b|mother|father|guardian/.test(t)) return 'parent';
  if (/teacher|\btrs\b/.test(t)) return 'teacher';
  if (/\bself\b|\bsrp\b/.test(t)) return 'self';
  return '';
}

function engineHasScores(row) {
  return Array.isArray(row?.score_rows) && row.score_rows.some((s) => s && s.name);
}

/** A scored Vineland / BASC engine row whose administration its own section already shows. */
function engineRowShownElsewhere(row, data) {
  if (!row || typeof row !== 'object' || !engineHasScores(row)) return false;
  const fam = ENGINE_FAMILY_SECTIONS.find((f) => f.re.test(String(row.test || '')));
  if (!fam) return false;
  const others = Array.isArray(data?.[fam.key]) ? data[fam.key] : [];
  const kind = engineRaterKind(`${row.form || ''} ${row.raters || ''}`);
  const day = engineDayNumber(row.date);
  return others.some((o) => {
    if (!o || typeof o !== 'object' || isEmptyAssessmentRow(o) || isVinelandCoverlessJunk(o)) return false;
    const oKind = engineRaterKind(`${o.responder_type || ''} ${o.test_name || ''} ${o.tool || ''} ${o.form || ''}`);
    if (kind && oKind && kind !== oKind) return false;
    const oDay = engineDayNumber(o.date);
    // Report covers and score pages can carry dates a few days apart for one administration.
    if (day != null && oDay != null) return Math.abs(day - oDay) <= 30;
    return Boolean(kind) && kind === oKind;
  });
}

function withEngineDuplicatesHidden(data) {
  const pm = data?.program_modifications;
  if (Array.isArray(pm) && pm.some((r) => r && r.item) && Array.isArray(data?.instructional) && data.instructional.length) {
    // the item/frequency table lists the same names
    data = { ...data, instructional: [] };
  }
  const rows = data?.standardized_assessments;
  if (!Array.isArray(rows) || !rows.length) return data;
  const kept = scoredEngineRows(rows).filter((r) => !engineRowShownElsewhere(r, data));
  return kept.length === rows.length ? data : { ...data, standardized_assessments: kept };
}

/** Only administrations with scores: scoreless rows are blank score forms or tests quoted in a narrative. */
function scoredEngineRows(items) {
  return (Array.isArray(items) ? items : []).filter((r) => r && typeof r === 'object' && engineHasScores(r));
}

function engineTableLabel(id) {
  return String(id || '').replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/** Composite rows with their subtests nested, then any subtests no read composite claims. */
function buildEngineScoreLayout(test, scores) {
  const composites = scores.filter((s) => ENGINE_COMPOSITE_TABLES.has(s.table));
  const parts = scores.filter((s) => ENGINE_PART_TABLES.has(s.table));
  const others = scores.filter((s) => !ENGINE_COMPOSITE_TABLES.has(s.table) && !ENGINE_PART_TABLES.has(s.table));
  const spec = ENGINE_COMPOSITE_PARTS.find((p) => p.re.test(String(test || '')));
  const lc = (s) => String(s || '').trim().toLowerCase();
  const readComposites = new Set(composites.map((c) => lc(c.name)));
  const ownerOf = new Map();
  (spec?.groups || []).forEach(([composite, members]) => {
    if (!readComposites.has(lc(composite))) return;
    members.forEach((m) => { if (!ownerOf.has(lc(m))) ownerOf.set(lc(m), lc(composite)); });
  });
  const childrenOf = new Map();
  const loose = [];
  parts.forEach((p) => {
    const owner = ownerOf.get(lc(p.name));
    if (owner) {
      if (!childrenOf.has(owner)) childrenOf.set(owner, []);
      childrenOf.get(owner).push(p);
    } else {
      loose.push(p);
    }
  });
  const otherTables = [];
  others.forEach((s) => {
    const id = s.table || 'other';
    let t = otherTables.find((x) => x.id === id);
    if (!t) { t = { id, rows: [] }; otherTables.push(t); }
    t.rows.push(s);
  });
  return {
    composites: composites.map((c) => ({ score: c, children: childrenOf.get(lc(c.name)) || [] })),
    loose,
    looseLabel: loose.length && composites.length
      ? `Other ${engineTableLabel(loose[0].table).toLowerCase()}`
      : engineTableLabel(loose[0]?.table || 'subtests'),
    otherTables,
  };
}

function VerifiedAssessmentCard({ row, compact = false }) {
  const [collapsed, setCollapsed] = useState(() => ({}));
  const [allCols, setAllCols] = useState(false);
  const [openTables, setOpenTables] = useState(() => ({}));
  const scores = (Array.isArray(row.score_rows) ? row.score_rows : []).filter((s) => s && s.name);
  const testName = row.test || 'Test';

  const cellCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} align-top text-gray-700 dark:text-slate-200`;
  const headCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;
  const metaCls = 'text-[11px] text-gray-500 dark:text-slate-400';

  const header = (
    <header className={`px-3 ${compact ? 'py-1.5' : 'py-2'} bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 flex flex-wrap items-baseline gap-x-4 gap-y-0.5`}>
      <h4 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900 dark:text-white`}>{testName}</h4>
      <span className={metaCls} title={row.date_source ? `Date read from ${row.date_source}` : undefined}>
        Date: {row.date || '—'}
      </span>
      <span className={metaCls}>Examiner: {row.examiner || 'N/A'}</span>
      {row.raters ? <span className={metaCls}>Respondent: {row.raters}</span> : null}
      {row.form ? <span className={metaCls}>Form: {row.form}</span> : null}
      <span className="ml-auto">
        <RowSourceChip row={row} label={testName} />
      </span>
    </header>
  );

  const layout = buildEngineScoreLayout(testName, scores);
  const tableScores = [
    ...layout.composites.flatMap((c) => [c.score, ...c.children]),
    ...layout.loose,
  ];
  const has = (key) => tableScores.some((s) => engineValuePresent(s.values?.[key]));
  const extraKeys = [...new Set(tableScores.flatMap((s) => Object.keys(s.values || {})))]
    .filter((k) => !ENGINE_KNOWN_COLS.has(k));
  const primaryCols = ENGINE_PRIMARY_COLS.filter(([k]) => has(k));
  const secondaryCols = [
    ...ENGINE_SECONDARY_COLS.filter(([k]) => has(k)),
    ...extraKeys.filter(has).map((k) => [k, engineTableLabel(k)]),
  ];
  const cols = allCols ? [...primaryCols, ...secondaryCols] : primaryCols;

  const formatValue = (key, v) => {
    if (!engineValuePresent(v)) return '—';
    return key === 'percentile' ? formatPercentileRank(v) : String(v);
  };

  const nestedAny = layout.composites.some((c) => c.children.length);
  const scoreRow = (s, { composite = false, nested = false, toggle = null, open = true, key }) => {
    const flagged = Array.isArray(s.checks) && s.checks.length > 0;
    const rowCls = flagged
      ? 'bg-amber-50/70 dark:bg-amber-900/20'
      : composite ? 'bg-indigo-50/40 dark:bg-indigo-950/20' : undefined;
    return (
      <tr key={key} className={rowCls} title={flagged ? s.checks.join('; ') : undefined}>
        <td className={`${cellCls} ${composite ? 'font-semibold' : 'font-medium'} ${nested ? 'pl-12' : ''}`}>
          <span className="inline-flex items-center gap-1.5">
              {toggle ? (
                <button
                  type="button"
                  onClick={toggle}
                  className="inline-flex h-5 w-5 items-center justify-center rounded border border-gray-200 dark:border-slate-600 text-gray-500 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-700"
                  aria-expanded={open}
                  title={open ? 'Hide subtest scores' : 'Show subtest scores'}
                >
                  <span className="text-[10px] leading-none">{open ? '▾' : '▸'}</span>
                </button>
              ) : composite && nestedAny ? <span className="inline-block h-5 w-5" aria-hidden="true" /> : null}
              <span>{s.name}</span>
              {flagged ? <span className="text-amber-600 dark:text-amber-400" aria-label="Failed checks">⚠</span> : null}
          </span>
        </td>
        {cols.map(([k]) => (
          <td key={k} className={`${cellCls} whitespace-nowrap tabular-nums`}>{formatValue(k, s.values?.[k])}</td>
        ))}
      </tr>
    );
  };

  const bandRow = (label, key) => (
    <tr key={key}>
      <td
        colSpan={cols.length + 1}
        className={`${cellCls} bg-gray-50/80 dark:bg-slate-900/60 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400`}
      >
        {label}
      </td>
    </tr>
  );

  const body = [];
  if (layout.composites.length && !nestedAny) {
    body.push(bandRow(engineTableLabel(layout.composites[0].score.table), 'band-c'));
  }
  layout.composites.forEach(({ score, children }, ci) => {
    const ckey = String(score.name).toLowerCase();
    const open = !collapsed[ckey];
    body.push(scoreRow(score, {
      composite: true,
      key: `c-${ci}`,
      open,
      toggle: children.length ? () => setCollapsed((prev) => ({ ...prev, [ckey]: !prev[ckey] })) : null,
    }));
    if (open) {
      children.forEach((sub, si) => body.push(scoreRow(sub, { nested: true, key: `c-${ci}-${si}` })));
    }
  });
  if (layout.loose.length) {
    if (layout.composites.length) body.push(bandRow(layout.looseLabel, 'band-l'));
    layout.loose.forEach((s, li) => body.push(scoreRow(s, { key: `l-${li}` })));
  }

  return (
    <article className="rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-800">
      {header}
      {tableScores.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`${headCls} min-w-[13rem]`}>Measure</th>
                {cols.map(([k, label]) => <th key={k} className={headCls}>{label}</th>)}
              </tr>
            </thead>
            <tbody>{body}</tbody>
          </table>
        </div>
      ) : null}
      {secondaryCols.length > 0 ? (
        <div className="px-3 py-1 border-t border-gray-100 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setAllCols((v) => !v)}
            className="text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            aria-pressed={allCols}
          >
            {allCols ? 'Fewer columns' : `All columns (+${secondaryCols.map(([, l]) => l).join(', ')})`}
          </button>
        </div>
      ) : null}
      {layout.otherTables.map((t) => {
        const open = !!openTables[t.id];
        const keys = [...new Set(t.rows.flatMap((s) => Object.keys(s.values || {})))]
          .filter((k) => t.rows.some((s) => engineValuePresent(s.values?.[k]) && String(s.values[k]) !== String(s.name)));
        return (
          <div key={t.id} className="border-t border-gray-100 dark:border-slate-700">
            <button
              type="button"
              onClick={() => setOpenTables((prev) => ({ ...prev, [t.id]: !prev[t.id] }))}
              className="w-full px-3 py-1.5 flex items-center gap-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-900/40"
              aria-expanded={open}
            >
              <span className="leading-none">{open ? '▾' : '▸'}</span>
              {engineTableLabel(t.id)} ({t.rows.length})
            </button>
            {open ? (
              <div className="overflow-x-auto px-3 pb-2">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className={headCls}>Measure</th>
                      {keys.map((k) => <th key={k} className={headCls}>{engineTableLabel(k)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.map((s, ri) => {
                      const flagged = Array.isArray(s.checks) && s.checks.length > 0;
                      return (
                        <tr key={ri} className={flagged ? 'bg-amber-50/70 dark:bg-amber-900/20' : undefined} title={flagged ? s.checks.join('; ') : undefined}>
                          <td className={`${cellCls} font-medium`}>
                            {s.name}{flagged ? <span className="ml-1 text-amber-600 dark:text-amber-400">⚠</span> : null}
                          </td>
                          {keys.map((k) => (
                            <td key={k} className={`${cellCls} whitespace-nowrap tabular-nums`}>{formatValue(k, s.values?.[k])}</td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        );
      })}
      {row.checks ? (
        <p className={`px-3 ${compact ? 'py-1.5 text-[11px]' : 'py-2 text-xs'} border-t border-amber-100 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10 text-amber-800 dark:text-amber-200`}>
          Review: {row.checks}
        </p>
      ) : null}
    </article>
  );
}

function VerifiedAssessmentList({ items, compact = false }) {
  const list = scoredEngineRows(items);
  if (!list.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {list.map((row, i) => (
        <VerifiedAssessmentCard key={`${row.test}-${row.date}-${i}`} row={row} compact={compact} />
      ))}
    </div>
  );
}

function CognitiveAssessmentCard({ value, compact = false }) {
  const obj = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  if (isWjCognitiveAssessment(obj)) {
    const tableRows = Array.isArray(obj.table_of_scores) ? obj.table_of_scores : [];
    const hasHeader = !isEmptyValue(obj.test_name) || !isEmptyValue(obj.date) || !isEmptyValue(obj.examiner) || !isEmptyValue(obj.norms);
    const gia = tableRows.find((r) => /^gen intellectual/i.test(String(r?.cluster_test || '')));
    if (!hasHeader && !tableRows.length) {
      return <span className="text-gray-400 dark:text-slate-500">—</span>;
    }
    return (
      <article className="rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-800">
        {hasHeader && (
          <header className={`px-3 ${compact ? 'py-1.5' : 'py-2'} bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 flex flex-wrap items-baseline gap-x-4 gap-y-0.5`}>
            <h4 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900 dark:text-white`}>
              {obj.test_name || obj.tool || 'Woodcock-Johnson IV Tests of Cognitive Abilities'}
            </h4>
            <span className="text-[11px] text-gray-500 dark:text-slate-400">Date: {obj.date || '—'}</span>
            <span className="text-[11px] text-gray-500 dark:text-slate-400">Examiner: {obj.examiner || '—'}</span>
            {obj.norms ? (
              <span className="text-[11px] text-gray-500 dark:text-slate-400">Norms: {obj.norms}</span>
            ) : null}
            {gia?.ss != null ? (
              <span className="text-[11px] text-gray-700 dark:text-slate-200">
                GIA: <span className="font-medium">{formatWjSsDisplay(gia)}</span>
              </span>
            ) : null}
          </header>
        )}
        <WjCognitiveScoreTable rows={tableRows} compact={compact} />
        <WjVariationTable title="VARIATIONS" rows={obj.variations} compact={compact} />
        <WjVariationTable title="COMPARISONS" rows={obj.comparisons} compact={compact} />
      </article>
    );
  }

  const indexes = COG_INDEX_ROWS
    .map(([key, label]) => {
      const sub = cognitiveIndexBlob(obj, key);
      if (!sub || typeof sub !== 'object' || Array.isArray(sub)) return null;
      if (isEmptyValue(sub.ss) && isEmptyValue(sub.percentile) && isEmptyValue(sub.description)
        && isEmptyValue(sub.printed_ss)) return null;
      return {
        key,
        label,
        ss: sub.ss,
        printed: !isEmptyValue(sub.printed_ss) && String(sub.printed_ss) !== String(sub.ss ?? '') ? sub.printed_ss : null,
        percentile: sub.percentile,
        description: cognitiveDescriptiveCategory(sub),
      };
    })
    .filter(Boolean);
  const chips = [
    ['FSIQ', typeof obj.fsiq === 'object' ? obj.fsiq?.ss : obj.fsiq],
    ['GAI', typeof obj.gai === 'object' ? obj.gai?.ss : obj.gai],
  ].filter(([, v]) => !isEmptyValue(v));
  const hasHeader = !isEmptyValue(obj.test_name) || !isEmptyValue(obj.date) || !isEmptyValue(obj.examiner);
  if (!hasHeader && !chips.length && !indexes.length) {
    return <span className="text-gray-400 dark:text-slate-500">—</span>;
  }

  const cellCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} align-top text-gray-700 dark:text-slate-200`;
  const headCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;

  return (
    <article className="rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden bg-white dark:bg-slate-800">
      {hasHeader && (
        <header className={`px-3 ${compact ? 'py-1.5' : 'py-2'} bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-600 flex flex-wrap items-baseline gap-x-4 gap-y-0.5`}>
          <h4 className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-gray-900 dark:text-white`}>
            {obj.test_name || 'Cognitive Assessment'}
          </h4>
          <span className="text-[11px] text-gray-500 dark:text-slate-400">Date: {obj.date || '—'}</span>
          <span className="text-[11px] text-gray-500 dark:text-slate-400">Examiner: {obj.examiner || '—'}</span>
        </header>
      )}
      {chips.length > 0 && (
        <div className="px-3 py-2 flex flex-wrap gap-1.5 border-b border-gray-100 dark:border-slate-700">
          {chips.map(([label, val]) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 rounded-md bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[11px] text-gray-700 dark:text-slate-200"
            >
              <span className="text-gray-500 dark:text-slate-400">{label}</span>
              <span className="font-medium">{String(val)}</span>
            </span>
          ))}
        </div>
      )}
      {indexes.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={headCls}>Index</th>
                <th className={headCls}>{SCORE_COL_STANDARD}</th>
                <th className={headCls}>{PERCENTILE_COL_LABEL}</th>
                <th className={headCls}>{LEVEL_COL_LABEL}</th>
              </tr>
            </thead>
            <tbody>
              {indexes.map((row) => (
                <tr key={row.key}>
                  <td className={`${cellCls} font-medium`}>{row.label}</td>
                  <td className={`${cellCls} whitespace-nowrap`}>
                    {row.ss || '—'}
                    {row.printed != null ? (
                      <span
                        className="ml-1 text-[10px] text-amber-700 dark:text-amber-300"
                        title="The document prints a score outside the usual 40–160 range; check it against the page."
                      >
                        (document: {String(row.printed)}, check)
                      </span>
                    ) : null}
                  </td>
                  <td className={`${cellCls} whitespace-nowrap`}>{row.percentile || '—'}</td>
                  <td className={cellCls}>{row.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

const OUTSIDE_WINDOW_REASONS = {
  before_evaluation: 'Dated before this evaluation',
  after_evaluation: 'Dated after this evaluation',
  other_cycle_pages: "Printed only on another IEP cycle's pages",
  other_administration: 'Another administration of this test',
  scores_not_read: 'In the documents; scores were not read',
};
const OUTSIDE_WINDOW_LIST_KEYS = {
  adaptive_assessment: 'adaptive_assessments',
  behavioral_assessment: 'behavioral_assessments',
};

const outsideRowTime = (x) => {
  const m = String(x?.date || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return m ? new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2])).getTime() : 0;
};

/** Rows the date and score filters kept out of this evaluation, shown as printed. */
function OutsideWindowAssessments({ items, schema, compact = false }) {
  const entries = (Array.isArray(items) ? items : [])
    .filter((x) => x && typeof x === 'object' && x.row && typeof x.row === 'object')
    .sort((a, b) => outsideRowTime(b) - outsideRowTime(a));
  if (!entries.length) return null;
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-slate-600 p-2">
      <p className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold text-gray-600 dark:text-slate-300`}>
        Other assessments in the documents
      </p>
      <p className="mb-2 text-[11px] text-gray-500 dark:text-slate-400">
        Shown as printed. They are not counted in this evaluation&apos;s compliance checks.
      </p>
      <div className={compact ? 'space-y-2' : 'space-y-3'}>
        {entries.map((x, i) => {
          const key = OUTSIDE_WINDOW_LIST_KEYS[x.section_key] || x.section_key;
          const field = schema.fields.find((f) => f.key === key);
          const single = field?.type === 'object';
          const normalized = safeNormalizeSectionExtractedData('evaluation', { [key]: single ? x.row : [x.row] });
          const value = normalized?.[key] ?? (single ? x.row : [x.row]);
          return (
            <div key={`${key}-${x.test || ''}-${x.date || ''}-${i}`}>
              <span className="mb-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                {OUTSIDE_WINDOW_REASONS[x.reason] || 'Outside this evaluation'}
              </span>
              {field && !isEmptyValue(value)
                ? <ReadOnlyFieldValue field={field} value={value} compact={compact} />
                : <ReadOnlyGenericValue value={x.row} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssessmentScoreTable({ items, compact = false }) {
  const list = Array.isArray(items)
    ? items.filter((r) => r && typeof r === 'object' && !isEmptyAssessmentRow(r))
    : [];
  if (!list.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;

  const cellCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} align-top text-gray-700 dark:text-slate-200`;
  const headCls = `border border-gray-200 dark:border-slate-600 px-2 ${compact ? 'py-1 text-[11px]' : 'py-1.5 text-xs'} bg-gray-50 dark:bg-slate-900 text-left font-semibold text-gray-500 dark:text-slate-400 whitespace-nowrap`;

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse">
        <thead>
          <tr>
            <th className={headCls}>Test</th>
            <th className={headCls}>Date</th>
            <th className={headCls}>Examiner</th>
            <th className={headCls}>Scores</th>
            <th className={headCls}>Results</th>
          </tr>
        </thead>
        <tbody>
          {list.map((row, i) => {
            const scores = Object.entries(row)
              .filter(([k, v]) => !isAssessmentMetaKey(k) && !isEmptyValue(v))
              .map(([k, v]) => {
                const s = formatScoreCell(v);
                return s ? `${formatKey(k)}: ${s}` : null;
              })
              .filter((s) => s && !s.includes('[object Object]'));
            if (row.scores && !Array.isArray(row.scores) && typeof row.scores !== 'object') {
              scores.push(String(row.scores));
            }
            const results = row.full_results || row.results || row.summary || '';
            return (
              <tr key={i}>
                <td className={`${cellCls} font-medium`}>{row.test_name || row.tool || '—'}</td>
                <td className={`${cellCls} whitespace-nowrap`}>
                  {row.date ? (
                    row.over_6_years ? (
                      <span className="text-amber-600 dark:text-amber-400" title="Assessment was over 6 years old at the evaluation date (visual only — not a compliance issue)">
                        {row.date}
                      </span>
                    ) : row.date
                  ) : '—'}
                </td>
                <td className={cellCls}>{row.examiner || '—'}</td>
                <td className={cellCls}>
                  {scores.length ? (
                    <ul className="space-y-0.5">
                      {scores.map((s, j) => <li key={j}>{s}</li>)}
                    </ul>
                  ) : '—'}
                </td>
                <td className={`${cellCls} whitespace-pre-wrap break-words max-w-[320px]`}>
                  {results ? sanitizeAssessmentNarrative(String(results)) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ComplianceStatusCell({ status }) {
  const text = formatDisplayValue(status) || '—';
  if (text === 'Needs Review' || text === 'Not Addressed') {
    return <span className="text-amber-700 dark:text-amber-400 font-medium">{text}</span>;
  }
  if (text === 'Addressed') {
    return <span className="text-gray-800 dark:text-slate-100">{text}</span>;
  }
  return <span className="text-gray-700 dark:text-slate-200">{text}</span>;
}

function ComplianceChecklistEditor({ items, onChange, fieldKey, statuses }) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  const showRequirements = fieldKey === 'transition_checklist';
  const selectCls = `${INPUT_CLS} py-1.5 text-xs`;
  const statusOptions = (Array.isArray(statuses) && statuses.length)
    ? statuses
    : COMPLIANCE_CHECKLIST_STATUSES;

  const updateStatus = (index, status) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, status } : row)));
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 dark:bg-slate-800/80">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300 w-[22%]">
              Compliance Area
            </th>
            {showRequirements && (
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">
                Compliance Requirements
              </th>
            )}
            <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300 w-[18%]">
              {showRequirements ? 'Compliance Status' : 'Status'}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row?.area || i} className="border-t border-gray-100 dark:border-slate-700/60 align-top">
              <td className="px-3 py-2 text-gray-800 dark:text-slate-100 font-medium">
                {row?.label || row?.area || '—'}
              </td>
              {showRequirements && (
                <td className="px-3 py-2 text-gray-600 dark:text-slate-300 leading-relaxed">
                  {row?.requirement || TRANSITION_CHECKLIST_REQUIREMENTS[row?.area] || '—'}
                </td>
              )}
              <td className="px-3 py-2">
                <select
                  value={row?.status ?? ''}
                  onChange={(e) => updateStatus(i, e.target.value || null)}
                  className={selectCls}
                  aria-label={`Status for ${row?.label || row?.area || 'row'}`}
                >
                  <option value="">—</option>
                  {statusOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SpecialFactorsGridEditor({ value, onChange }) {
  const sf = value && typeof value === 'object' ? value : {};

  const setFactor = (key, enumValue) => {
    onChange({ ...sf, [key]: enumValue });
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 dark:bg-slate-800/80">
          <tr>
            {SPECIAL_FACTOR_GRID.map((col) => (
              <th
                key={col.key}
                className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-slate-300 align-top border-r border-gray-100 dark:border-slate-700/60 last:border-r-0"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-gray-100 dark:border-slate-700/60">
            {SPECIAL_FACTOR_GRID.map((col) => {
              const selected = sf[col.key];
              const noChecked = selected === col.noValue;
              return (
                <td
                  key={`${col.key}-no`}
                  className="px-2 py-2 align-top border-r border-gray-100 dark:border-slate-700/60 last:border-r-0"
                >
                  <EditableSpecialFactorOption
                    label={col.no}
                    checked={noChecked}
                    onSelect={() => setFactor(col.key, col.noValue)}
                  />
                </td>
              );
            })}
          </tr>
          <tr className="border-t border-gray-100 dark:border-slate-700/60">
            {SPECIAL_FACTOR_GRID.map((col) => {
              const selected = sf[col.key];
              const yesChecked = selected === col.yesValue;
              return (
                <td
                  key={`${col.key}-yes`}
                  className="px-2 py-2 align-top border-r border-gray-100 dark:border-slate-700/60 last:border-r-0"
                >
                  <EditableSpecialFactorOption
                    label={col.yes}
                    checked={yesChecked}
                    onSelect={() => setFactor(col.key, col.yesValue)}
                  />
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function EditableSpecialFactorOption({ label, checked, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex items-start gap-1.5 leading-snug text-left w-full rounded-md px-1 py-0.5 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors ${
        checked ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-slate-400'
      }`}
    >
      <span className="shrink-0 mt-px">{checked ? '☑' : '☐'}</span>
      <span>{label}</span>
    </button>
  );
}

function checklistDetailLines(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return [];
  return Object.entries(details)
    .filter(([k, v]) => !k.startsWith('_') && v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${formatKey(k)}: ${formatDisplayValue(v) || String(v)}`);
}

function ReadOnlyComplianceChecklist({ items, fieldKey }) {
  const rows = Array.isArray(items) ? items : [];
  if (!rows.length) return <span className="text-gray-400 dark:text-slate-500">—</span>;
  const showRequirements = fieldKey === 'transition_checklist';
  const showDetails = rows.some((row) => checklistDetailLines(row?.details).length > 0);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 dark:bg-slate-800/80">
          <tr>
            <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300 w-[22%]">
              Compliance Area
            </th>
            {showRequirements && (
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">
                Compliance Requirements
              </th>
            )}
            <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300 w-[18%]">
              {showRequirements ? 'Compliance Status' : 'Status'}
            </th>
            {showDetails && (
              <th className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-slate-300">
                Details
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const detailLines = showDetails ? checklistDetailLines(row?.details) : [];
            return (
              <tr key={row?.area || i} className="border-t border-gray-100 dark:border-slate-700/60 align-top">
                <td className="px-3 py-2 text-gray-800 dark:text-slate-100 font-medium">
                  {row?.label || row?.area || '—'}
                </td>
                {showRequirements && (
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300 leading-relaxed">
                    {row?.requirement || TRANSITION_CHECKLIST_REQUIREMENTS[row?.area] || '—'}
                  </td>
                )}
                <td className="px-3 py-2">
                  <ComplianceStatusCell status={row?.status} />
                </td>
                {showDetails && (
                  <td className="px-3 py-2 text-gray-600 dark:text-slate-300">
                    {detailLines.length ? (
                      <ul className="space-y-0.5">
                        {detailLines.map((line) => <li key={line}>{line}</li>)}
                      </ul>
                    ) : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const TRANSITION_CHECKLIST_REQUIREMENTS = {
  transition_assessment: 'An age-appropriate transition assessment has been completed and used to inform transition planning',
  postsecondary_goal_education: 'A measurable postsecondary goal is identified for education and/or training after high school',
  postsecondary_goal_employment: 'A measurable postsecondary goal is identified for employment after high school',
  postsecondary_goal_independent_living: 'The IEP documents a measurable postsecondary goal for independent living when independent living is an appropriate area of need for the student. If independent living is not an area of need, the IEP should indicate that an independent-living goal is not necessary.',
  transition_services: 'Transition services are identified that will reasonably enable the student to meet the postsecondary goals. There should be transition services for each postsecondary goal. These transition services should be provided by the school.',
  course_of_study: "A course of study is identified and is aligned with the student's postsecondary goals",
  student_participation: 'The student is invited to the IEP meeting when transition services are being discussed',
  agency_participation: 'When appropriate, participating agencies are invited, with required consent, when an agency may pay for transition services. (Ex. Vocational Rehabilitation Services etc.)',
};

const SPECIAL_FACTOR_GRID = [
  {
    key: 'language_needs',
    label: 'Language Needs (LEP)',
    noValue: 'no_lep_needs',
    yesValue: 'lep_addressed_in_iep',
    no: 'No LEP Needs',
    yes: 'LEP needs addressed in IEP',
  },
  {
    key: 'braille_needs',
    label: 'Braille Needs',
    noValue: 'no_braille_needed',
    yesValue: 'braille_addressed_in_iep',
    no: 'No Braille needed',
    yes: 'Braille instruction addressed in IEP',
  },
  {
    key: 'communication_needs',
    label: 'Communication Needs',
    noValue: 'no_communication_needs',
    yesValue: 'communication_addressed_in_iep',
    no: 'No Communication needs',
    yes: 'Communication needs addressed in IEP',
  },
  {
    key: 'assistive_technology',
    label: 'Assistive Technology Needs',
    noValue: 'no_at_needs',
    yesValue: 'at_addressed_in_iep',
    no: 'No Assistive Technology needs',
    yes: 'Assistive Technology needs addressed in the IEP',
  },
  {
    key: 'behavior_strategy',
    label: 'Behavior Strategy Needs',
    noValue: 'no_behavior_needs',
    yesValue: 'behavior_addressed_in_iep',
    no: 'No Behavior Strategy needs',
    yes: 'Behavior Strategies addressed in the IEP',
  },
];

function SpecialFactorCheckbox({ label, checked }) {
  return (
    <div className={`flex items-start gap-1.5 leading-snug ${checked ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-slate-400'}`}>
      <span className="shrink-0 mt-px">{checked ? '☑' : '☐'}</span>
      <span>{label}</span>
    </div>
  );
}

function ReadOnlySpecialFactorsGrid({ value }) {
  const sf = value && typeof value === 'object' ? value : {};
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
        <thead className="bg-gray-50 dark:bg-slate-800/80">
          <tr>
            {SPECIAL_FACTOR_GRID.map((col) => (
              <th
                key={col.key}
                className="text-left px-2 py-2 font-semibold text-gray-600 dark:text-slate-300 align-top border-r border-gray-100 dark:border-slate-700/60 last:border-r-0"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-gray-100 dark:border-slate-700/60">
            {SPECIAL_FACTOR_GRID.map((col) => {
              const selected = sf[col.key];
              const noChecked = selected === col.noValue;
              const yesChecked = selected === col.yesValue;
              return (
                <td
                  key={`${col.key}-no`}
                  className="px-2 py-2 align-top border-r border-gray-100 dark:border-slate-700/60 last:border-r-0"
                >
                  <SpecialFactorCheckbox label={col.no} checked={noChecked} />
                </td>
              );
            })}
          </tr>
          <tr className="border-t border-gray-100 dark:border-slate-700/60">
            {SPECIAL_FACTOR_GRID.map((col) => {
              const selected = sf[col.key];
              const yesChecked = selected === col.yesValue;
              return (
                <td
                  key={`${col.key}-yes`}
                  className="px-2 py-2 align-top border-r border-gray-100 dark:border-slate-700/60 last:border-r-0"
                >
                  <SpecialFactorCheckbox label={col.yes} checked={yesChecked} />
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ReadOnlyFieldValue({ field, value, compact = false }) {
  if (field.display === 'check_na') {
    return <CheckNaValue value={value} />;
  }
  if (isEmptyValue(value)) return <span className="text-gray-400 dark:text-slate-500">—</span>;

  switch (field.type) {
    case 'string_list': {
      const list = Array.isArray(value)
        ? value
        : (typeof value === 'string' && value.trim() ? [value] : []);
      return (
        <ul className="list-disc list-inside space-y-0.5 text-sm">
          {list.map((item, i) => (
            <li key={i} className="text-gray-700 dark:text-slate-200">{formatDisplayValue(item)}</li>
          ))}
        </ul>
      );
    }
    case 'object_list':
      if (field.key === 'standardized_assessments') {
        return <VerifiedAssessmentList items={value} compact={compact} />;
      }
      if (ASSESSMENT_TABLE_KEYS.has(field.key)) {
        if (field.key === 'academic_standardized') {
          return <AcademicBatteryList items={value} compact={compact} />;
        }
        if (field.key === 'classroom_assessments') {
          return <ClassroomBatteryList items={value} compact={compact} />;
        }
        if (field.key === 'behavioral_assessments') {
          return <BehavioralBatteryList items={value} compact={compact} />;
        }
        if (field.key === 'adaptive_assessments') {
          return <AdaptiveBatteryList items={value} compact={compact} />;
        }
        return <AssessmentScoreTable items={value} compact={compact} />;
      }
      return <ReadOnlyObjectList items={value} fieldDef={field} compact={compact} />;
    case 'text': {
      const displayText = typeof value === 'string'
        ? sanitizeAssessmentNarrative(value)
        : (typeof value === 'object' && value !== null ? formatDisplayValue(value) : String(value ?? ''));
      if (!displayText) return <span className="text-gray-400 dark:text-slate-500">—</span>;
      return (
        <p className="text-sm text-gray-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words">
          {displayText}
        </p>
      );
    }
    case 'boolean':
      if (field.display === 'check_na') {
        return <CheckNaValue value={value} />;
      }
      return <span className="text-sm">{value ? 'Yes' : 'No'}</span>;
    case 'object':
      if (field.key === 'cognitive_assessment') {
        return <CognitiveAssessmentCard value={unwrapSingleObject(value)} compact={compact} />;
      }
      return <ReadOnlyObjectEditor value={unwrapSingleObject(value)} fieldDef={field} compact={compact} />;
    case 'assessment_data_table':
      return <AssessmentDataTable value={value} compact={compact} />;
    case 'present_levels_table':
      return <PresentLevelsTable value={value} compact={compact} />;
    case 'goals_client_tables':
      return <GoalsClientTables value={value} compact={compact} />;
    case 'compliance_checklist':
      return <ReadOnlyComplianceChecklist items={value} fieldKey={field?.key} />;
    case 'special_factors_grid':
      return <ReadOnlySpecialFactorsGrid value={value} />;
    case 'dibels_table':
      return <DibelsTableEditor value={value} />;
    case 'json': {
      if (field?.key === 'chart_profiles') {
        return <ReadOnlyChartProfiles value={value} compact={compact} />;
      }
      const unwrapped = unwrapSingleObject(value);
      if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
        return <ReadOnlyObjectEditor value={unwrapped} fieldDef={{ fields: [] }} />;
      }
      return (
        <pre className="text-xs text-gray-700 dark:text-slate-200 whitespace-pre-wrap break-words font-sans overflow-x-auto">
          {JSON.stringify(unwrapped, null, 2)}
        </pre>
      );
    }
    case 'date':
      return (
        <span className={`${compact ? 'text-[11px]' : 'text-sm'} text-gray-700 dark:text-slate-200 break-words`}>
          {formatChecklistDate(value)}
        </span>
      );
    case 'string':
      if (Array.isArray(value)) {
        return (
          <ul className="list-disc list-inside space-y-0.5 text-sm">
            {value.map((item, i) => (
              <li key={i} className="text-gray-700 dark:text-slate-200">{formatDisplayValue(item)}</li>
            ))}
          </ul>
        );
      }
      if (typeof value === 'object') {
        return <span className="text-sm text-gray-700 dark:text-slate-200">{formatDisplayValue(value)}</span>;
      }
      return <span className="text-sm text-gray-700 dark:text-slate-200 break-words">{String(value)}</span>;
    default:
      if (Array.isArray(value)) {
        return (
          <ul className="list-disc list-inside space-y-0.5 text-sm">
            {value.map((item, i) => (
              <li key={i} className="text-gray-700 dark:text-slate-200">{formatDisplayValue(item)}</li>
            ))}
          </ul>
        );
      }
      if (typeof value === 'object') {
        return <span className="text-sm text-gray-700 dark:text-slate-200">{formatDisplayValue(value)}</span>;
      }
      return <span className="text-sm text-gray-700 dark:text-slate-200 break-words">{String(value)}</span>;
  }
}

export default function SectionExtractedDataEditor({
  sectionKey,
  data,
  onChange,
  readOnly = false,
  compact = false,
  onOpenFileAtPage = null,
  onOpenSource = null,
  sourceFileId = null,
  sourceFileName = null,
}) {
  const [onlyUnsourced, setOnlyUnsourced] = useState(false);
  const schema = getSchemaForSection(sectionKey);
  const rawSourcePages = (
    data && typeof data.field_source_pages === 'object' && !Array.isArray(data.field_source_pages)
  ) ? data.field_source_pages : {};
  const evidenceSpans = (
    data && typeof data._evidence_spans === 'object' && !Array.isArray(data._evidence_spans)
  ) ? data._evidence_spans : {};
  const fieldCite = (field) => citeForField(
    field.key,
    rawSourcePages,
    evidenceSpans,
    field.fallbackKeys,
  );
  const citeProps = (field) => {
    const c = fieldCite(field);
    return {
      onOpenFileAtPage,
      pages: c.pages,
      fileId: c.fileId ?? sourceFileId,
      fileName: c.fileName ?? sourceFileName,
      quote: c.quote,
    };
  };

  const updateField = (key, val) => onChange({ ...data, [key]: val });

  if (!schema) {
    if (readOnly) {
      return (
        <div>
          <label className="block text-xs font-semibold text-gray-900 dark:text-white mb-2">Extracted Data</label>
          <p className="text-sm text-gray-500 dark:text-slate-400">—</p>
        </div>
      );
    }
    return (
      <div>
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">Extracted Data</label>
        <p className="text-sm text-gray-500 dark:text-slate-400">No form schema for this section.</p>
      </div>
    );
  }

  if (readOnly) {
    const viewData = withEngineDuplicatesHidden(data);
    const visibleFields = schema.fields.filter(
      (f) => !f.hidden && !isSchemaFieldHidden(sectionKey, f.key) && (
        f.type === 'goals_client_tables' || !isEmptyValue(valueForField(viewData, f))
      ),
    );
    const fullWidthKeys = new Set([
      'team_participation',
      'eligibility_form_checkboxes',
      'team_members',
      'eligibility_records',
      'transition_checklist',
      'iep_compliance_checklist',
      'special_factors',
    ]);
    const tableFields = visibleFields.filter((f) => (
      f.type === 'present_levels_table'
      || f.type === 'assessment_data_table'
      || f.type === 'goals_client_tables'
      || f.type === 'compliance_checklist'
      || f.type === 'special_factors_grid'
      || fullWidthKeys.has(f.key)
    ));
    const otherFields = visibleFields.filter((f) => !tableFields.includes(f));
    // Keys already consumed by a schema field as a fallback (e.g. iep_end_date feeding
    // "Annual IEP Due Date") must not repeat under Additional Data.
    const fallbackKeys = new Set(schema.fields.flatMap((f) => f.fallbackKeys || []));
    const extraKeys = Object.keys(data).filter(
      (k) =>
        !schema.fields.some((f) => f.key === k) &&
        !fallbackKeys.has(k) &&
        !isHiddenExtractedKey(k) &&
        !isRetiredExtraKey(sectionKey, k) &&
        !isEmptyValue(data[k]),
    );

    if (visibleFields.length === 0 && extraKeys.length === 0) {
      return (
        <p className="text-sm text-gray-400 dark:text-slate-500 italic">
          No extracted data yet. Use Edit on this section to add fields manually.
        </p>
      );
    }

    // ---- citations: one cite per displayed field, the section's J/K sequence, coverage
    const openSource = onOpenSource || (onOpenFileAtPage
      ? (req) => onOpenFileAtPage(req.fileId, req.page, req.fileName, req.quote)
      : null);
    const citeOf = new Map(visibleFields.map((f) => [
      f.key, resolveFieldCite(data, [f.key, ...(f.fallbackKeys || [])]),
    ]));
    const scalarText = (v) => (
      v == null || typeof v === 'object' || typeof v === 'boolean' ? null : String(v)
    );
    const citable = visibleFields.filter((f) => !CITE_EXEMPT_KEYS.has(f.key));
    const sourcedCount = citable.filter((f) => citeOf.get(f.key)?.kind !== 'missing').length;
    const unsourced = citable.filter((f) => citeOf.get(f.key)?.kind === 'missing');
    const sequence = visibleFields
      .map((f) => ({ f, c: citeOf.get(f.key) }))
      .filter(({ c }) => c && c.pages?.length && c.kind !== 'derived')
      .map(({ f, c }) => ({
        fileId: c.fileId ?? sourceFileId,
        fileName: c.fileName ?? sourceFileName,
        page: firstQuotePage(c) || c.pages[0],
        quote: quoteForPage(c, firstQuotePage(c) || c.pages[0]),
        label: f.label,
        value: scalarText(valueForField(data, f)),
        kind: c.kind,
      }));
    const seqIndex = new Map(sequence.map((s, i) => [s.label, i]));
    const chipFor = (field) => {
      if (CITE_EXEMPT_KEYS.has(field.key)) return null;
      const cite = citeOf.get(field.key);
      return (
        <CitationChip
          cite={cite}
          label={field.label}
          value={scalarText(valueForField(data, field))}
          sequence={sequence}
          index={seqIndex.get(field.label) ?? 0}
        />
      );
    };
    const shown = (f) => !onlyUnsourced || citeOf.get(f.key)?.kind === 'missing';
    const shownTableFields = tableFields.filter(shown);
    const shownOtherFields = otherFields.filter(shown);
    const pct = citable.length ? Math.round((100 * sourcedCount) / citable.length) : 100;
    const citationContextValue = {
      openSource,
      defaultFileId: sourceFileId,
      defaultFileName: sourceFileName,
    };

    const labelCellCls = compact
      ? 'px-2 py-1.5 text-[11px] font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 w-[38%] align-top'
      : 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 w-1/3 align-top';
    const valueCellCls = compact
      ? 'px-2 py-1.5 text-[11px] text-gray-700 dark:text-slate-200 align-top'
      : 'px-3 py-2 text-sm text-gray-700 dark:text-slate-200 align-top';

    return (
      <CitationContext.Provider value={citationContextValue}>
      <div className={compact ? 'space-y-2' : 'space-y-3'}>
        {citable.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 bg-gray-50/70 px-3 py-1.5 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-slate-300">
              <span className="relative inline-block h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700" aria-hidden="true">
                <span
                  className={`absolute inset-y-0 left-0 rounded-full ${pct >= 85 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-rose-500'}`}
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span>
                <span className="font-semibold tabular-nums">{sourcedCount}</span> of{' '}
                <span className="tabular-nums">{citable.length}</span> values sourced
              </span>
              {sequence.length > 1 && openSource ? (
                <span className="hidden text-gray-400 dark:text-slate-500 md:inline">· open any page chip, then J / K to step through</span>
              ) : null}
            </div>
            {unsourced.length > 0 ? (
              <button
                type="button"
                onClick={() => setOnlyUnsourced((v) => !v)}
                className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${onlyUnsourced
                  ? 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-200'
                  : 'border-gray-200 text-gray-600 hover:bg-white dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800'}`}
                aria-pressed={onlyUnsourced}
              >
                {onlyUnsourced ? 'Show all fields' : `Show unsourced (${unsourced.length})`}
              </button>
            ) : null}
          </div>
        ) : null}
        {shownTableFields.map((field) => (
          <div key={field.key} className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-x-auto p-2">
            {(fullWidthKeys.has(field.key) || field.type === 'compliance_checklist' || field.type === 'special_factors_grid' || field.type === 'assessment_data_table' || field.type === 'present_levels_table') && field.label ? (
              <div className={`${compact ? 'mb-1' : 'mb-2'} flex items-center justify-between gap-2`}>
                <p className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold text-gray-600 dark:text-slate-300`}>{field.label}</p>
                {chipFor(field)}
              </div>
            ) : (
              <div className="mb-1 flex justify-end">{chipFor(field)}</div>
            )}
            <ReadOnlyFieldValue field={field} value={valueForField(viewData, field)} compact={compact} />
          </div>
        ))}
        {shownOtherFields.length > 0 && (
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-x-auto">
            <table className="w-full min-w-[240px]">
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {shownOtherFields.map((field) => (
                  <tr key={field.key}>
                    <td className={labelCellCls}>{field.label}</td>
                    <td className={valueCellCls}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <ReadOnlyFieldValue field={field} value={valueForField(viewData, field)} compact={compact} />
                        </div>
                        <div className="shrink-0 pt-0.5">{chipFor(field)}</div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sectionKey === 'evaluation' && !onlyUnsourced ? (
          <OutsideWindowAssessments items={data._outside_window_rows} schema={schema} compact={compact} />
        ) : null}
        {onlyUnsourced && !shownTableFields.length && !shownOtherFields.length ? (
          <p className="text-xs text-gray-500 dark:text-slate-400">Every value in this section has a source.</p>
        ) : null}
        {extraKeys.length > 0 && !HIDE_EXTRA_DATA_PANEL.has(sectionKey) && (
          <div className="pt-2 border-t border-gray-100 dark:border-slate-700">
            <p className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2">{extraDataHeading(sectionKey)}</p>
            <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-x-auto">
              <table className="w-full min-w-[280px]">
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {extraKeys.map((key) => (
                    <tr key={key}>
                      <td className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900 w-1/3 align-top">
                        {formatKey(key)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-slate-200 align-top">
                        <ReadOnlyGenericValue value={data[key]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      </CitationContext.Provider>
    );
  }

  const extraKeys = Object.keys(data).filter(
    (k) =>
      sectionKey !== 'present_levels' &&
      !schema.fields.some((f) => f.key === k) &&
      !isHiddenExtractedKey(k) &&
      !isRetiredExtraKey(sectionKey, k),
  );

  const EDIT_WIDE_TYPES = new Set([
    'text', 'string_list', 'object_list', 'object', 'json', 'dibels_table',
    'assessment_data_table', 'present_levels_table', 'goals_client_tables',
    'compliance_checklist', 'special_factors_grid',
  ]);
  const EDIT_WIDE_KEYS = new Set([
    'cognitive_assessment',
    'academic_standardized',
    'classroom_assessments',
    'adaptive_assessments',
    'behavioral_assessments',
  ]);
  const isWideEditField = (field) => (
    EDIT_WIDE_TYPES.has(field.type)
    || EDIT_WIDE_KEYS.has(field.key)
    || (field.type === 'object_list' && ASSESSMENT_TABLE_KEYS.has(field.key))
  );
  const editFields = schema.fields.filter((f) => !f.hidden && !isSchemaFieldHidden(sectionKey, f.key));

  return (
    <div>
      <p className="text-xs font-semibold text-gray-700 dark:text-slate-300 mb-2">
        {sectionKey === 'present_levels' ? schema.label : `${schema.label} — edit`}
      </p>
      <div className="space-y-2">
        {editFields.map((field) => {
          if (isWideEditField(field)) {
            return (
              <div key={field.key} className="rounded-lg border border-gray-200 dark:border-slate-700 p-2">
                <p className="text-[11px] font-semibold text-gray-600 dark:text-slate-300 mb-1.5">
                  <FieldLabelWithPages label={field.label} {...citeProps(field)} />
                </p>
                <FieldInput
                  fieldDef={field}
                  value={valueForField(data, field)}
                  onChange={(val) => updateField(field.key, val)}
                />
              </div>
            );
          }
          return (
            <div
              key={field.key}
              className="grid grid-cols-1 sm:grid-cols-[minmax(130px,34%)_1fr] gap-1 sm:gap-2 items-start py-1 border-b border-gray-100 dark:border-slate-800 last:border-0"
            >
              <label className="text-[11px] font-medium text-gray-500 dark:text-slate-400 sm:pt-1">
                <FieldLabelWithPages label={field.label} {...citeProps(field)} />
              </label>
              <FieldInput
                fieldDef={field}
                value={valueForField(data, field)}
                onChange={(val) => updateField(field.key, val)}
                compact
              />
            </div>
          );
        })}
        {extraKeys.length > 0 && !HIDE_EXTRA_DATA_PANEL.has(sectionKey) && (
          <div className="pt-3 border-t border-gray-100 dark:border-slate-700">
            <p className="text-xs text-gray-400 dark:text-slate-500 mb-2">Additional fields (saved with this section only)</p>
            {extraKeys.map((key) => (
              <div key={key} className="text-xs text-gray-500 dark:text-slate-400 py-0.5">{formatKey(key)}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { pruneForSave, VerifiedAssessmentCard };
